import {isPercentage, isValidPositiveFraction} from '../components/configComponents/styleDesignBoard/utils';
import {DESIGNBOARD, MOUSE_ACTION_COMP_RESIZE} from "../constant/common"
import {astStore} from '../store'
import {calculateFraction, calculatePercentage, convertInlineCss2Number} from '../utils';
import {
  camelToKebab,
  getRemValue,
  kebabToCamel,
  parseCssPx2TailwindRem,
  parseTailwindToCss,
} from '../utils/tailwindcss';
import {
  ALIGN_CONTENT, ALIGN_ITEMS,
  BD_RADIUS,
  BG_COLOR,
  DISPLAY,
  FLEX_DIRECTION,
  FLEX_WRAP, GRID_COLUMN, GRID_JUSTIFY_ITEMS, GRID_JUSTIFY_SELF, GRID_ROW,
  GRID_TEMPLATE_COLUMNS,
  GRID_TEMPLATE_ROWS,
  HEIGHT,
  JUSTIFY_CONTENT,
  LEFT, MARGIN,
  MARGIN_BOTTOM,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  MARGIN_TOP,
  MAX_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH, PADDING,
  PADDING_BOTTOM,
  PADDING_LEFT,
  PADDING_RIGHT,
  PADDING_TOP,
  POSITION,
  RIGHT,
  TOP,
  WIDTH,
  Z_INDEX
} from '../components/configComponents/styleDesignBoard/constant';
import {updateASTComponents} from "../store/common/action";

/**
 *
 * @description 更新自己和 children 的 z-index
 * @param { string } prop
 * @param { number } upperZIndex
 * @param { ComponentItem[] } $compsCopy 引用
 */
const updateZindex = (prop: string, upperZIndex: number, $compsCopy: ComponentItem[]) => {
  const comp = $compsCopy.find(c => c.prop === prop)
  if (!comp) return
  const {children, dynamicCss: [{sources: [{css: {normal: {'z-index': indexTwFmt = 'z-0'}}}]}]} = comp
  const [_, indexAst] = indexTwFmt.split('-')
  let indexAstNumber = +indexAst
  if (indexAstNumber <= upperZIndex) {
    indexAstNumber++
    _set(comp, `dynamicCss[0].sources[0].css.normal['z-index']`, `z-${indexAstNumber}`)
  }
  if (children?.length) {
    children.forEach(({prop: childProp}) => {
      updateZindex(childProp, indexAstNumber, $compsCopy)
    })
  }
}


// 递归往上找，返回 position 不为 static 的父组件（absolute 是相对于不为 static 的偏移）
const findParentCompWithSpecificPosi = (prop: string) => {
  const root = 'root'
  const {components} = astStore.state
  if (!prop || prop == root) {
    // 找不到默认root
    return components.find(c => c.prop === root)
  }
  // 查找父节点
  const s_comp = components.find(c => c.prop === prop)
  if (!s_comp.parent || s_comp.parent == root) {
    return components.find(c => c.prop === root)
  }
  const p_comp = components.find(c => c.prop === s_comp.parent)
  const p_dycss = p_comp.dynamicCss[0].sources[0].css.normal
  if (p_dycss[POSITION] && p_dycss[POSITION] != 'static') {
    return p_comp
  }
  return findParentCompWithSpecificPosi(p_comp.parent)
}


/**
 * 生成子节点树索引
 * @param {string} parentIndex - 父节点索引，如 "1.1"
 * @param {number} childOrder - 子节点顺序（从1开始）
 * @returns {string} 子节点索引，如 "1.1.1"
 */
const generateTreeNodeIndex = (parentIndex, childOrder) => {
  return `${parentIndex}.${childOrder}`
}

/**
 * ⭐⭐⭐
 * @param { string } prop 当前组件的 prop
 * @param { boolean } isHidden 当前组件是否应该设置 display: none
 * @param parentIndex
 * @param orderInParent
 * @param comps
 * @description 更新设计区（全量循环更新），每次处理一个 component
 * @description web-components 中偏移量（top/left/right/bottom）说明：
 * 1 设计态中，从真实 DOM 上获取的内联样式的偏移量（如，top/left 等）position 为 absolute 的 DOM 是相对于 root，fixed 相对于视口；
 * 2 设计态中，所有组件对应的 DOM 元素都是平级的，样式都用内联样式。预览时组件对应的 DOM 元素是嵌套关系；
 * 3 设计态中，从 AST 上 dynamicCss 中偏移量（如，top/left）
 *  a. 若该组件的 position 为 absolute，则偏移量（如，top/left）是该组件距离最近的 position 不为 static 的父级的距离；
 *  b. 若该组件的 position 为 fixed，则偏移量（如，top/left）是对根组件（root）的距离，但该组件仍然有自己的父级；
 *  c. 若该组件的 position 为 static，则偏移量（如，top/left）没意义。该组件在其直接父级中占据文档流；
 *  d. 若该组件的 position 为 relative，则偏移量（如，top/left）是针对自身文档流位置的偏移。该组件在其直接父级中占据文档流；
 *  e. 若该组件的 position 为 sticky，该组件根据正常文档流进行定位，然后根据最近的滚动祖先和最近块级祖先进行偏移（如，top/left/right/bottom），
 *    i. 滚动到最近块级祖先距离(top/left/right/bottom)时会吸附；
 * 4 现象：centreCanvasComp.tsx 中监听 components 变化，就执行 recurseCalcCompsXY
 *  a. 设计区内，若手动拖拽某个组件，则该组件就会停留在拖动后的位置，然后根据这个位置和（新）父级来计算并修改 AST 中的 dynamicCss 的偏移（top/left）。<先有位置、再更新 ast、后取 ast 渲染>
 *  b. 设计态中，若选中某个组件，然后修改其 position 的属性值（如，从 absolute 改成 fixed），则该组件会瞬移到设计区中的某个位置，正常现象。<更新 ast、取 ast 渲染>
 * @returns
 *
 */
export const recurseCalcCompsXY = (prop: string = 'root', isHidden: boolean = false, parentIndex = '1', orderInParent = 1, comps = null) => {
  // 计算不修改 ast
  const { components } = comps == null ? astStore.state : { components: comps }
  if (!prop) return

  // 从 root 开始，从根到叶子节点全部更新
  const s_comp = components.find(c => c.prop === prop)
  // 父组件
  const p_el = document.getElementById(
    s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent
  )

  // 生成当前节点索引
  let currentIndex = '1'
  if (prop !== 'root') {
    currentIndex = generateTreeNodeIndex(parentIndex, orderInParent)
  }

  let s_dycss = parseToCssDefault(s_comp)
  // ⭐ 检查当前节点是否隐藏（自身隐藏或父级隐藏）
  const currentHidden = isHidden || s_dycss[DISPLAY] === 'none'

  if (prop == 'root') {
    // root 节点只作为设计屏幕分辨率
    calcRootNodeStyle(s_comp)
  } else {
    // 普通节点
    calcNormalNodeStyle(prop, s_comp, s_dycss, p_el, currentIndex, isHidden, currentHidden, components)
  }

  // ⭐⭐ 递归子级
  calcChildren(s_comp, currentIndex, currentHidden, components, comps)
}

/**
 * 处理 root 节点：只作为设计屏幕分辨率，设置设计板的宽高、背景、定位等。
 * 逻辑与原代码 root 分支完全一致。
 */
function calcRootNodeStyle(s_comp: any) {
  const default_w = 1920
  const default_h = 1080
  const s_el = document.getElementById(DESIGNBOARD)

  // 设置 root 索引
  s_el.dataset.index = '0'

  const s_dycss = parseToCssDefault(s_comp)
  const s_w = s_dycss[WIDTH]
  const s_h = s_dycss[HEIGHT]

  // 宽度处理
  if (!s_w || ['full', 'auto', 'screen', '100vh', '100vw', '100%'].includes(s_w)) {
    s_el.style.width = default_w + 'px'
  } else {
    if (isPercentage(s_w)) {
      // 处理百分比
      s_el.style.width = calculatePercentage(s_w) * default_w + 'px'
    } else if (isValidPositiveFraction(s_w)) {
      s_el.style.width = calculateFraction(s_w) * default_w + 'px'
    } else {
      s_el.style.width = s_w
    }
  }

  // 高度处理
  if (!s_h || ['full', 'auto', 'screen', '100vh', '100vw', '100%'].includes(s_h)) {
    s_el.style.height = default_h + 'px'
  } else {
    if (isPercentage(s_h)) {
      // 处理百分比
      s_el.style.height = calculatePercentage(s_h) * default_h + 'px'
    } else if (isValidPositiveFraction(s_h)) {
      s_el.style.height = calculateFraction(s_h) * default_h + 'px'
    } else {
      s_el.style.height = s_h
    }
  }

  s_el.style.position = 'relative'
  s_el.style.display = 'block'

  if (s_dycss[BG_COLOR]) {
    // 背景色
    s_el.style.backgroundColor = s_dycss[BG_COLOR]
  }
}

/**
 * 处理普通节点：树索引、隐藏、特殊类型、尺寸、定位、剩余样式。
 */
function calcNormalNodeStyle(prop: string, s_comp: any, s_dycss: any, p_el: HTMLElement | null, currentIndex: string, isHidden: boolean, currentHidden: boolean, components: any[]
) {
  // 对已处理好的样式，不再自动处理
  const styleFilters = new Set<string>()

  // ⭐⭐ AST 中的偏移量（如，left/top 等）都是相对于最近的 position 不为 static 的父级的偏移
  const s_el = document.getElementById(prop)
  if (!s_el) return

  // ===== 设置树索引 =====
  s_el.dataset.index = currentIndex
  s_el.dataset.depth = String(currentIndex.split('.').length)

  // 如果当前节点隐藏，只设置 display: none，不计算其他样式
  if (currentHidden) {
    s_el.style.setProperty(DISPLAY, 'none')
    // 清空其他样式，避免影响布局
    s_el.style.top = ''
    s_el.style.left = ''
    s_el.style.width = ''
    s_el.style.height = ''
    s_el.style.position = 'absolute'
    s_el.style.zIndex = ''
    s_el.style.top = ''
    // 设置 data 标记便于调试
    s_el.dataset.hidden = 'true'
  }

  // 类型为圆的原子组件，设置为圆角；路径不设置背景色
  if (s_comp.type == 'circle') {
    s_el.style.borderRadius = '50%'
    styleFilters.add(BD_RADIUS)
  }
  if (s_comp.type == 'path') {
    styleFilters.add(BG_COLOR)
  }

  // 清空 margin，并加入 filter
  s_el.style.margin = ''
  s_el.style.marginLeft = ''
  s_el.style.marginRight = ''
  s_el.style.marginTop = ''
  s_el.style.marginBottom = ''
  styleFilters.add(MARGIN).add(MARGIN_LEFT).add(MARGIN_RIGHT).add(MARGIN_TOP).add(MARGIN_BOTTOM)

  // 有层级就用 AST 的层级
  s_el.style.zIndex =
    convertInlineCss2Number(p_el?.style.zIndex) + 1 + convertInlineCss2Number(s_dycss[Z_INDEX]) + ''
  styleFilters.add(Z_INDEX)

  // 设计态的 dom 的 position 为 absolute
  s_el.style.setProperty(POSITION, 'absolute')
  styleFilters.add(POSITION)

  // 显隐藏，默认不隐藏
  if (isHidden || s_dycss[DISPLAY] == 'none') {
    isHidden = true
    s_el.style.setProperty(DISPLAY, 'none')
  }
  if (isHidden) {
    styleFilters.add(DISPLAY)
  }

  // 宽高
  s_el.style.setProperty(WIDTH, calculateWidth(s_dycss, s_comp, components))
  styleFilters.add(WIDTH)
  s_el.style.setProperty(HEIGHT, calculateHeight(s_dycss, s_comp, components))
  styleFilters.add(HEIGHT)

  // top / left
  calculateTop(s_el, s_dycss, s_comp, components)
  styleFilters.add(TOP)
  calculateLeft(s_el, s_dycss, s_comp, components)
  styleFilters.add(LEFT)

  // 剩余样式
  for (let [k, v] of Object.entries(s_dycss)) {
    if (styleFilters.has(kebabToCamel(k))) continue
    s_el.style.setProperty(camelToKebab(k), v as string)
  }
}

/**
 * 递归子级。
 *
 */
function calcChildren(s_comp: any, currentIndex: string, currentHidden: boolean, components: any[], comps: any) {
  let childOrder = 2
  for (let i = 0; i < s_comp.children.length; i++) {
    const childRef = s_comp.children[i]
    const childComp = components.find(c => c.prop === childRef.prop)
    if (!childComp) continue
    // ⭐ 检查子节点是否隐藏
    const childDycss = parseToCssDefault(childComp)
    const childHidden = currentHidden || childDycss[DISPLAY] === 'none'

    // 子节点顺序从 1 开始
    const isSticky =
      getPositionType(childComp) != 'static' &&
      getPositionType(childComp) == 'sticky' &&
      getPositionType(childComp) != 'relative'

    recurseCalcCompsXY(
      childRef.prop,
      childHidden,
      currentIndex, // 当前节点索引作为父索引
      isSticky ? childOrder++ : 1, // 子节点顺序
      comps
    )
  }
}


/**
 * 获取组件的可见子节点（过滤掉隐藏的子节点）
 * 用于各种布局计算
 */
export const getVisibleChildrenForLayout = (comp, comps) => {
  if (!comp || !comp.children || comp.children.length === 0) {
    return []
  }

  const visibleChildren = []
  for (const childRef of comp.children) {
    const childComp = comps.find(c => c.prop === childRef.prop)
    if (!childComp) continue

    const childDycss = parseToCssDefault(childComp)
    // ⭐ 只返回 display 不是 none 的子节点
    if (childDycss[DISPLAY] !== 'none') {
      visibleChildren.push(childComp)
    }
  }
  return visibleChildren
}

/**
 * 检查组件是否隐藏
 */
export const isComponentHidden = (comp) => {
  if (!comp) return true
  const dycss = parseToCssDefault(comp)
  return dycss[DISPLAY] === 'none'
}

/**
 * 计算left
 * @param s_el
 * @param s_dycss
 * @param s_comp
 * @param components
 */
const calculateLeft = (s_el, s_dycss, s_comp, components) => {
  // Top 都是相对父级位置，并且与posistion关联
  const s_position = s_dycss[POSITION]
  const s_ml = convertInlineCss2Number(s_dycss[MARGIN_LEFT])
  const s_right = s_dycss[RIGHT]

  let result = null;

  if (!s_position || s_position == 'static' || s_position == 'sticky') {
    // 确定父级
    const p_comp = components.find(c => c.prop === s_comp.parent);
    const p_dycss = parseToCssDefault(p_comp)
    const p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
    if ("grid" == p_dycss[DISPLAY] && !["absolute", "fixed"].includes(s_position)) {
      let re_left = convertInlineCss2Number(p_el.style.left)  // 父的Left坐标
      let s_left_value = calculateGridLeft(s_comp, s_dycss, p_dycss, components)
      s_el.style.left = (s_left_value + re_left) + 'px'
      result = (s_left_value + re_left);
    } else if (p_dycss[DISPLAY] == 'flex') {
      result = calculateFlexLeft(s_dycss, s_comp, p_comp, p_dycss, p_el, components)
    } else {
      let p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
      // 默认的位置都是父级
      const p_el_left = convertInlineCss2Number(p_el.style.left)
      const {paddingLeft: p_pl} = parseToCssDefault(components.find(c => c.prop === s_comp.parent))
      let s_left_value = p_el_left + convertInlineCss2Number(p_pl) + s_ml
      if (s_position == 'sticky') {
        s_left_value += convertInlineCss2Number(s_dycss[LEFT])
      }
      s_el.style.left = s_left_value + 'px'
      result = s_left_value
    }
  } else if (s_position == 'relative' || s_position == 'absolute' || s_position == 'fixed') {
    // 先确定父级
    let s_left = s_dycss[LEFT]
    let p_dycss, p_el, p_comp
    if (s_position == 'fixed') {
      p_el = document.getElementById(DESIGNBOARD)
      p_dycss = parseToCssDefault(components.find(c => c.prop === 'root'))
      p_comp = components.find(c => c.prop === 'root')
    }
    if (s_position == 'relative') {
      if (!s_left) {
        s_left = s_dycss[MARGIN_LEFT]
      }
      p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
      p_dycss = parseToCssDefault(components.find(c => c.prop === s_comp.parent))
      p_comp = components.find(c => c.prop === s_comp.parent)
      if ("grid" == p_dycss[DISPLAY]) {
        s_left = calculateGridLeft(s_comp, s_dycss, p_dycss, components)
      }
    }
    if (s_position == 'absolute') {
      // absolute的位置是最上非static
      p_comp = findParentCompWithSpecificPosi(s_comp.prop);
      p_el = document.getElementById(p_comp.prop === 'root' ? DESIGNBOARD : p_comp.prop)
      p_dycss = parseToCssDefault(p_comp)
      if ("grid" == p_dycss[DISPLAY]) {
        s_left = calculateGridLeft(s_comp, s_dycss, p_dycss, components)
      }
    }

    let re_left = convertInlineCss2Number(p_el.style.left)
    if (p_dycss[DISPLAY] == 'flex') {
      re_left = calculateFlexLeft(s_dycss, s_comp, p_comp, p_dycss, p_el, components)
    }

    let s_left_value = 0
    // const p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
    if (isValidPositiveFraction(s_left)) {
      s_left_value += calculateFraction(s_left) * calculateFraction(p_el.style.left)
    } else {
      let leftValue = convertInlineCss2Number(s_left)
      const s_el = document.getElementById(s_comp.prop)
      if (s_right) {
        leftValue = re_left + convertInlineCss2Number(p_el.style.width) - convertInlineCss2Number(s_right) - convertInlineCss2Number(s_el.style.width)
      }
      s_left_value += leftValue + re_left
    }
    s_el.style.left = s_left_value + 'px'
    result = s_left_value;
  }
  return result;
}

/**
 * 计算flex left
 * @param s_dycss
 * @param s_comp
 * @param p_comp
 * @param p_dycss
 * @param p_el
 * @param comps
 */
const calculateFlexLeft = (s_dycss, s_comp, p_comp, p_dycss, p_el, comps) => {
  const p_el_left = convertInlineCss2Number(p_el.style.left)
  // 当前节点 top 值（默认返回父级 top）
  let leftValue = p_el_left
  // 非文档流元素（absolute/fixed）不参与 Flex 布局计算
  if (s_dycss[POSITION] == 'absolute' || s_dycss[POSITION] == 'fixed') {
    return leftValue
  }
  let p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
  let p_pr = convertInlineCss2Number(p_dycss[PADDING_RIGHT])
  if (p_pl === 0 || p_pr === 0) {
    const {left, right} = parsePadding(p_dycss[PADDING])
    p_pl = left || 0
    p_pr = right || 0
  }
  const p_gap_x = convertInlineCss2Number(p_el.style.columnGap)
  const p_el_w = convertInlineCss2Number(p_el.style.width)
  // 换行，使用二维数组
  const prev_full_calcs = []
  for (const {prop: prev_prop} of p_comp.children) {
    // 获取所有可计算节点
    const prev_comp = comps.find(c => c.prop === prev_prop)
    const prev_dycss = parseToCssDefault(prev_comp)
    if (!prev_dycss[POSITION] || prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'sticky' || prev_dycss[POSITION] == 'relative') {
      const prev_el = document.getElementById(prev_prop)

      // 检测 margin-left 是否为 auto
      const mlValue = prev_dycss[MARGIN_LEFT]
      const isMlAuto = mlValue === 'auto'
      const prev_ml = isMlAuto ? 0 : convertInlineCss2Number(mlValue)

      // 检测 margin-right 是否为 auto
      const mrValue = prev_dycss[MARGIN_RIGHT]
      const isMrAuto = mrValue === 'auto'
      const prev_mr = isMrAuto ? 0 : convertInlineCss2Number(mrValue)

      const child = {
        prev_prop: prev_prop,
        prev_el: prev_el,
        prev_ml: prev_ml,
        isMlAuto: isMlAuto,
        prev_mr: prev_mr,
        prev_el_w: convertInlineCss2Number(prev_el.style.width),
        prev_gap_x: 0,
        prev_gap_y: 0,
        row_space: 0
      }
      prev_full_calcs.push(child)
    }
  }
  // 水平反转
  const is_row = !p_dycss[FLEX_DIRECTION] || p_dycss[FLEX_DIRECTION] == 'row'
  const is_row_reverse = p_dycss[FLEX_DIRECTION] == 'row-reverse'
  // 垂直
  const is_col = p_dycss[FLEX_DIRECTION] == 'column' || p_dycss[FLEX_DIRECTION] == 'col'
  // 垂直反转
  const is_col_reverse = p_dycss[FLEX_DIRECTION] == 'column-reverse' || p_dycss[FLEX_DIRECTION] == 'col-reverse'
  // 换行
  const is_warp = p_dycss[FLEX_WRAP] == 'wrap'
  // 换行反转
  const is_warp_reverse = p_dycss[FLEX_WRAP] == 'wrap-reverse'
  // 主轴默认
  const is_justify_start = p_dycss[JUSTIFY_CONTENT] == 'flex-start' || p_dycss[JUSTIFY_CONTENT] == 'start'
  // 主轴end
  const is_justify_end = p_dycss[JUSTIFY_CONTENT] == 'flex-end'
  // 主轴居中
  const is_justify_center = p_dycss[JUSTIFY_CONTENT] == 'center'
  // 交叉轴居中
  const is_items_center = p_dycss[ALIGN_ITEMS] == 'center'
  // 主轴均衡
  const is_justify_between = p_dycss[JUSTIFY_CONTENT] == 'space-between' || p_dycss[JUSTIFY_CONTENT] == 'between'

  // 分组
  const prev_rows = []
  let prev_warp_w = p_pl + p_pr, prev_rows_index = 0
  for (let i = 0; i < prev_full_calcs.length; i++) {
    const child = prev_full_calcs[i]
    if (i > 0) {
      // 第一个元素不加间距
      child.prev_gap_x = p_gap_x
    }
    if (is_col || is_col_reverse) {
      // 垂直分组
      prev_rows_index = i
      child.prev_el_w = child.prev_el_w + p_pl + p_pr
    } else if (is_warp || is_warp_reverse) {
      // 换行分组
      prev_warp_w += child.prev_el_w + child.prev_ml + child.prev_mr + child.prev_gap_x
      if (prev_warp_w > p_el_w) {
        prev_rows_index++
        // 发生换行后重置行空间计量
        child.prev_gap_x = 0
      }
    }
    if (!prev_rows[prev_rows_index]) {
      prev_rows[prev_rows_index] = {
        rows: [],
        col_space: 0 // 列容量
      }
    }
    prev_rows[prev_rows_index].rows.push(child)
  }

  if (!(is_col || is_col_reverse)) {
    // 水平宽度
    for (const prev_row of prev_rows) {
      let max_w = 0, re_w = 0, p_re_w = p_el_w - p_pl - p_pr
      for (const row of prev_row.rows) {
        max_w += row.prev_el_w
      }
      if (max_w > p_re_w) {
        // 超宽度
        for (const row of prev_row.rows) {
          const childComp = comps.find(c => c.prop === row.prev_prop);
          const childDycss = parseToCssDefault(childComp);
          const childWidth = childDycss[WIDTH];
          const hasFixedWidth = childWidth &&
            !['auto', 'full', 'screen', '100%'].includes(childWidth);
          if (hasFixedWidth) {
            // 最大宽度按比例父宽计算
            re_w = row.prev_el_w - p_pl - p_pr
            re_w = re_w - (row.prev_ml + row.prev_mr)
            row.prev_el.style.width = re_w + 'px'
            row.prev_el_w = re_w
          } else {
            row.prev_el.style.width = '';
            const actualWidth = row.prev_el.getBoundingClientRect().width
            // 判断是否有文字内容
            const hasText = row.prev_el.textContent.trim().length > 0;
            if (actualWidth > 0 && hasText && ['auto'].includes(childWidth)) {
              row.prev_el.style.width = actualWidth + 'px';
              row.prev_el_w = actualWidth;
            } else {
              re_w = (row.prev_el_w / max_w) * p_re_w
              re_w = re_w - (row.prev_ml + row.prev_mr + row.prev_gap_x)
              row.prev_el.style.width = re_w + 'px'
              row.prev_el_w = re_w
            }
          }

        }
      }
    }
  }

  // 计算 row_space
  if (is_justify_start || is_row || is_row_reverse) {
    for (const prev_row of prev_rows) {
      let row_space = 0
      for (const row of prev_row.rows) {
        // 计算 row_w 时使用实际的 margin 值（auto 的情况已在前面处理为 0）
        const row_w = row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x
        if (is_row_reverse) {
          row_space += row_w
          row.row_space = row.prev_ml + row.prev_gap_x + row_space
        } else {
          row.row_space = row.prev_ml + row.prev_gap_x + row_space
          row_space += row_w
        }
      }
    }
  }

  if (is_justify_end && !(is_col || is_col_reverse)) {
    for (const prev_row of prev_rows) {
      let row_w_max = 0
      for (const row of prev_row.rows) {
        row_w_max += row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x
      }
      let row_space_free = p_el_w - row_w_max
      for (const row of prev_row.rows) {
        const row_w = row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x
        if (is_row_reverse) {
          row_space_free += row_w
          row.row_space = row.prev_ml + row.prev_gap_x + row_space_free
        } else {
          row.row_space = row.prev_ml + row.prev_gap_x + row_space_free
          row_space_free += row_w
        }
      }
    }
  }

  if (is_justify_between && !(is_col || is_col_reverse)) {
    // 主轴均衡
    for (const prev_row of prev_rows) {
      let row_w_total = 0
      for (const row of prev_row.rows) {
        row_w_total += row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x
      }
      let row_space_free = (p_el_w - row_w_total) / (prev_row.rows.length - 1)
      let row_space_next = 0
      for (const row of prev_row.rows) {
        if (is_row_reverse) {
          row_space_next += row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x
          row.row_space = row_space_next
          row_space_next += row_space_free
        } else {
          row.row_space = row_space_next
          row_space_next += row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x + row_space_free
        }

      }
    }
  }

  if ((is_justify_center && (is_row || is_row_reverse)) || (is_items_center && (is_col || is_col_reverse))) {
    // 主轴居中，交叉轴居中
    for (const prev_row of prev_rows) {
      const row_space_total = prev_row.rows.reduce((total, row) => total + row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x, 0);
      prev_row.col_space = (p_el_w - row_space_total) / 2
    }
  }

  // 当前组最大宽度
  let re_left = 0
  for (const {col_space, rows} of prev_rows) {
    // 计算 ml-auto 剩余空间
    let totalWidth = 0, mlAutoCount = 0
    for (const row of rows) {
      totalWidth += row.prev_el_w + row.prev_ml + row.prev_mr + row.prev_gap_x
      if (row.isMlAuto) mlAutoCount++
    }
    const mlAutoSpace = mlAutoCount > 0 ? Math.max(0, (p_el_w - p_pl - p_pr) - totalWidth) / mlAutoCount : 0

    // row_space（用 actualMl 替换 prev_ml，保留原有 gap 计算）
    let row_space_acc = 0
    for (const row of rows) {
      const actualMl = row.isMlAuto ? mlAutoSpace : row.prev_ml
      const row_w = row.prev_el_w + actualMl + row.prev_mr + row.prev_gap_x
      if (is_row_reverse) {
        row_space_acc += row_w
        row.row_space = actualMl + row.prev_gap_x + row_space_acc
      } else {
        row.row_space = actualMl + row.prev_gap_x + row_space_acc
        row_space_acc += row_w
      }
    }
    for (const {prev_prop, prev_el, row_space} of rows) {
      if (is_row_reverse) {
        re_left = p_el_left + p_el_w - p_pr - col_space - row_space
      } else {
        re_left = p_el_left + p_pl + col_space + row_space
      }
      if (prev_prop == s_comp.prop) {
        leftValue = re_left
      }
      prev_el.style.left = re_left + 'px'
    }
  }

  return leftValue
}

/**
 * 计算top
 * @param s_el
 * @param s_dycss
 * @param s_comp
 * @param comps
 */
const calculateTop = (s_el, s_dycss, s_comp, comps) => {
  let value = 0
  // Top 都是相对父级位置，并且与position关联
  const s_position = s_dycss[POSITION]
  const s_mt = convertInlineCss2Number(s_dycss[MARGIN_TOP])
  const s_top = s_dycss[TOP]
  let result = null;
  // relative和sticky特殊处理
  if (!s_position || s_position == 'static' || s_position == 'sticky') {
    const p_comp = comps.find(c => c.prop === s_comp.parent);
    const p_dycss = parseToCssDefault(p_comp)
    const p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
    // 默认的位置都是父级
    if ("grid" == p_dycss[DISPLAY]) {
      // 默认的位置都是父级
      result = calculateGridTop(s_comp, s_dycss, p_dycss, comps)
    } else if (p_dycss[DISPLAY] == 'flex') {
      result = calculateFlexTop(s_dycss, s_comp, p_comp, p_dycss, p_el, comps)
    } else {
      result = calculateDefaultStaticTop(s_comp, comps)
    }
  } else if (s_position == 'absolute' || s_position == 'fixed') {
    let p_el
    if (s_position == 'fixed') {
      p_el = document.getElementById(DESIGNBOARD)
    } else {
      // absolute的位置只能是相当于最近的relative或absolute的父节点，递归往上找。返回 position 不为 static 的父组件（absolute 是相对于不为 static 的偏移）
      const p_comp = findParentCompWithSpecificPosi(s_comp.prop);
      p_el = document.getElementById(p_comp.prop === 'root' ? DESIGNBOARD : p_comp.prop)
    }
    let p_el_top = convertInlineCss2Number(p_el.style.top)
    if (isValidPositiveFraction(s_top)) {  // 验证是否为严格意义上的正分数（必须包含分数线，分子分母都是正整数，分子不为0）
      // 计算字符串的分数数值
      value += calculateFraction(s_top) * p_el_top
    } else {
      value += convertInlineCss2Number(s_top) + p_el_top + s_mt
    }
    s_el.style.setProperty(TOP, value + 'px')
    result = value;
  } else if (s_position == 'relative') {
    const p_comp = comps.find(c => c.prop === s_comp.parent);
    const p_dycss = parseToCssDefault(p_comp)
    const p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
    const p_el_h = convertInlineCss2Number(p_el.style.height)
    let re_top = 0
    if ("grid" == p_dycss[DISPLAY]) {
      // 默认的位置都是父级
      re_top = calculateGridTop(s_comp, s_dycss, p_dycss, comps)
    } else if ("flex" == p_dycss[DISPLAY]) {
      re_top = calculateFlexTop(s_dycss, s_comp, p_comp, p_dycss, p_el, comps)
    } else {
      console.log("772====================================",s_comp,comps)
      re_top = calculateDefaultRelativeTop(s_comp, comps);
      console.log("re_top------",re_top)
    }
    const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
    // 计算relative当前的相对位置
    if (isValidPositiveFraction(s_top)) {
      value += calculateFraction(s_top) * p_el_h + re_top + p_pt
    } else {
      value += convertInlineCss2Number(s_top) + re_top
    }
    s_el.style.setProperty(TOP, value + 'px')
    result = value
  }
  console.log("result----------------------top",result)
  return result;
}

/**
 * 计算flex top
 * @param s_dycss
 * @param s_comp
 * @param p_comp
 * @param p_dycss
 * @param p_el
 * @param comps
 */
const calculateFlexTop = (s_dycss, s_comp, p_comp, p_dycss, p_el, comps) => {
  const p_el_top = convertInlineCss2Number(p_el.style.top)
  // 当前节点 top 值（默认返回父级 top）
  let topValue = p_el_top
  // 非文档流元素（absolute/fixed）不参与 Flex 布局计算
  if (s_dycss[POSITION] == 'absolute' || s_dycss[POSITION] == 'fixed') {
    return topValue
  }
  const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
  const p_pb = convertInlineCss2Number(p_dycss[PADDING_BOTTOM])
  const p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
  const p_pr = convertInlineCss2Number(p_dycss[PADDING_RIGHT])
  const p_gap_y = convertInlineCss2Number(p_el.style.rowGap) // 行间距
  const p_gap_x = convertInlineCss2Number(p_el.style.columnGap) //列间距
  let p_el_h = convertInlineCss2Number(p_el.style.height)
  const p_el_w = convertInlineCss2Number(p_el.style.width)
  // 获取可见子节点（过滤隐藏的）
  const visibleChildren = getVisibleChildrenForLayout(p_comp, comps)
  // 换行，使用二维数组
  const prev_full_calcs = []
  for (const {prop: prev_prop} of visibleChildren) {
    // 获取所有可计算节点
    const prev_comp = comps.find(c => c.prop === prev_prop)
    const prev_dycss = parseToCssDefault(prev_comp)
    if (!prev_dycss[POSITION] || prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'sticky' || prev_dycss[POSITION] == 'relative') {
      const prev_el = document.getElementById(prev_prop)
      if (!prev_el || prev_el.style.display === 'none') continue
      const child = {
        prev_prop: prev_prop,
        prev_el: prev_el,
        prev_ml: convertInlineCss2Number(prev_dycss[MARGIN_LEFT]),
        prev_mr: convertInlineCss2Number(prev_dycss[MARGIN_RIGHT]),
        prev_mt: convertInlineCss2Number(prev_dycss[MARGIN_TOP]),
        prev_mb: convertInlineCss2Number(prev_dycss[MARGIN_BOTTOM]),
        prev_el_w: convertInlineCss2Number(prev_el.style.width),
        prev_el_h: convertInlineCss2Number(prev_el.style.height),
        prev_gap_x: 0,
        prev_gap_y: 0,
        row_space: 0
      }
      prev_full_calcs.push(child)
    }
  }

  // 水平反转
  const is_row = !p_dycss[FLEX_DIRECTION] || p_dycss[FLEX_DIRECTION] == 'row'
  const is_row_reverse = p_dycss[FLEX_DIRECTION] == 'row-reverse'
  // 垂直
  const is_col = p_dycss[FLEX_DIRECTION] == 'column' || p_dycss[FLEX_DIRECTION] == 'col'
  // 垂直反转
  const is_col_reverse = p_dycss[FLEX_DIRECTION] == 'column-reverse' || p_dycss[FLEX_DIRECTION] == 'col-reverse'
  // 换行
  const is_warp = p_dycss[FLEX_WRAP] == 'wrap'
  // 换行反转
  const is_warp_reverse = p_dycss[FLEX_WRAP] == 'wrap-reverse'
  // 主轴居中
  const is_justify_center = p_dycss[JUSTIFY_CONTENT] == 'center'
  const is_justify_start = p_dycss[JUSTIFY_CONTENT] == 'start'
  const is_content_stretch = p_dycss[ALIGN_CONTENT] == 'stretch'
  // 交叉轴居中
  const is_items_center = p_dycss[ALIGN_ITEMS] == 'center'
  // 分组
  const prev_rows = []
  let prev_warp_w = p_pl + p_pr, prev_rows_index = 0
  for (let i = 0; i < prev_full_calcs.length; i++) {
    const child = prev_full_calcs[i]
    if (i > 0) {
      // 第一个元素不加间距
      child.prev_gap_x = p_gap_x
      child.prev_gap_y = p_gap_y
    }
    if (prev_rows_index != 0) {
      // 间距加到上区域
      child.p_gap_y = p_gap_y
    }
    if (is_col || is_col_reverse) {
      // 垂直分组
      prev_rows_index = i
    } else if (is_warp || is_warp_reverse) {
      // 换行分组
      prev_warp_w += child.prev_el_w + child.prev_ml + child.prev_mr + child.prev_gap_x
      if (prev_warp_w > p_el_w) {
        prev_rows_index++
        // 发生换行后重置行空间计量
        child.prev_gap_x = 0
        prev_warp_w = p_pl + p_pr + child.prev_el_w + child.prev_ml + child.prev_mr + child.prev_gap_x
      }
    }
    if (!prev_rows[prev_rows_index]) {
      prev_rows[prev_rows_index] = {
        rows: [],
        col_space: 0 // 列容量
      }
    }
    prev_rows[prev_rows_index].rows.push(child)
  }
  // ========== Column 模式 ==========
  if (is_col || is_col_reverse) {
    // 垂直高度
    let max_h = 0, re_h = 0, p_re_h = p_el_h - p_pt - p_pb
    for (const prev_row of prev_rows) {
      for (const row of prev_row.rows) {
        max_h += row.prev_el_h
      }
    }
    if (max_h > p_re_h) {
      // 超高度
      for (const prev_row of prev_rows) {
        for (const row of prev_row.rows) {
          // 最大宽度按比例父宽计算
          re_h = (row.prev_el_h / max_h) * p_re_h
          re_h = re_h - (row.prev_mt + row.prev_mb + row.prev_gap_y)
          row.prev_el.style.height = re_h + 'px'
          row.prev_el_h = re_h
        }
      }
    }
  } else if (!p_dycss[HEIGHT]) {
    // 不设高度时，为总行高
    let re_p_h = 0
    for (const prev_row of prev_rows) {
      let row_h_max = 0
      for (const row of prev_row.rows) {
        row_h_max = findMax([row_h_max, (row.prev_el_h + row.prev_mt + row.prev_mb + row.prev_gap_y)])
      }
      re_p_h += row_h_max
    }
    p_el.style.height = re_p_h + 'px'
    p_el_h = re_p_h
  }

  if (is_justify_start || is_col || is_col_reverse || is_row || is_row_reverse) {
    // 主轴起点对齐,默认对齐方式
    // Column 模式的 col_space 累加
    let col_space_next = 0
    for (const prev_row of prev_rows) {
      let row_space_max = 0
      for (const row of prev_row.rows) {
        const row_h = row.prev_el_h + row.prev_mt + row.prev_mb + row.prev_gap_y
        row_space_max = findMax([row_space_max, row_h])
        row.row_space = row.prev_mt + row.prev_gap_y
      }
      if (is_col_reverse) {
        col_space_next += row_space_max
        prev_row.col_space = col_space_next
      } else {
        prev_row.col_space = col_space_next
        col_space_next += row_space_max
      }
    }
  }

  if ((is_content_stretch || (is_warp || is_warp_reverse)) && !(is_col || is_col_reverse)) {
    // 交叉轴拉伸，flex换行默认使用这种方式
    // 默认分配剩余空间
    const free_space_defalut = p_el_h / prev_rows.length
    let col_space_next = 0
    for (const prev_row of prev_rows) {
      let row_space_max = 0
      for (const row of prev_row.rows) {
        const row_h = row.prev_el_h + row.prev_mt + row.prev_mb + row.prev_gap_y
        row_space_max = findMax([row_space_max, row_h])
        row.row_space = row.prev_mt + row.prev_gap_y
      }
      if (is_warp_reverse) {
        // 全部可用高度只会加到下区域
        col_space_next += row_space_max > free_space_defalut ? row_space_max : free_space_defalut
        prev_row.col_space = col_space_next
      } else {
        prev_row.col_space = col_space_next
        col_space_next += row_space_max > free_space_defalut ? row_space_max : free_space_defalut
      }

    }
  }

  if ((is_justify_center && (is_col || is_col_reverse))) {
    // 垂直居中 2y+D=h,D
    let col_space_total = 0 // 行总高度
    let col_space_next = 0
    for (const prev_row of prev_rows) {
      let row_space_max = 0
      for (const row of prev_row.rows) {
        const row_h = row.prev_el_h + row.prev_mt + row.prev_mb + row.prev_gap_y
        row_space_max = findMax([row_space_max, row_h])
        row.row_space = row.prev_mt + row.prev_gap_y
      }
      col_space_total += row_space_max
      if (is_col_reverse) {
        col_space_next += row_space_max
        prev_row.col_space = col_space_next
      } else {
        prev_row.col_space = col_space_next
        col_space_next += row_space_max
      }
    }
    // 剩余高度
    let free_col_space = (p_el_h - col_space_total) / 2
    for (const prev_row of prev_rows) {
      prev_row.col_space += free_col_space
    }
  }


  if (is_items_center && !(is_col || is_col_reverse)) {
    // 交叉轴居中，每行最大距居中
    let free_space_defalut = p_el_h / prev_rows.length // 每行默认最大高度
    let col_space_next = 0
    for (const prev_row of prev_rows) {
      let row_space_max = 0
      for (const row of prev_row.rows) {
        const row_space = row.prev_el_h + row.prev_mt + row.prev_mb + row.prev_gap_y
        row_space_max = findMax([row_space_max, row_space])
      }
      const col_space = row_space_max > free_space_defalut ? row_space_max : free_space_defalut
      for (const row of prev_row.rows) {
        const row_space = row.prev_el_h + row.prev_mt + row.prev_mb + row.prev_gap_y
        row.row_space = (col_space - row_space) / 2 + row.prev_mt + row.prev_gap_y
      }
      if (is_warp_reverse) {
        col_space_next += col_space
        prev_row.col_space = col_space_next
      } else {
        prev_row.col_space = col_space_next
        col_space_next += col_space
      }
    }

  }

  // 当前组最大高度
  let re_top = 0
  for (const {rows, col_space} of prev_rows) {
    for (const {prev_prop, prev_el, row_space} of rows) {
      if (is_col) {
        // 垂直
        re_top = p_el_top + p_pt + col_space + row_space
      } else if (is_col_reverse) {
        // 垂直反转
        re_top = p_el_top + p_el_h - p_pb - col_space - row_space
      } else if (is_warp_reverse) {
        re_top = p_el_top + p_el_h - p_pb - col_space + row_space
      } else {
        re_top = p_el_top + p_pt + col_space + row_space
      }

      if (prev_prop == s_comp.prop) {
        topValue = re_top
      }
      prev_el.style.top = re_top + 'px'
    }
  }
  return topValue
}

/**
 * 计算 Grid 布局中组件的 top 值
 * 按照 Grid 规则（从左到右、从上到下）计算每个子元素的行位置
 */
const calculateGridTop = (s_comp, s_dycss, p_dycss, components) => {
  // 筛选参与 Grid 布局的子组件，记录跨列和跨行信息
  const p_childs = components.find(c => c.prop === s_comp.parent).children
  // 筛选所有符合的子组件
  let prev_childs = []
  for (const p_child of p_childs) {
    const prev_comp = components.find(c => c.prop === p_child.prop)
    const prev_dycss = parseToCssDefault(prev_comp)
    if (prev_dycss[DISPLAY] === 'none') continue
    if (['static', 'relative', 'sticky', 'fixed'].includes(prev_dycss[POSITION])) {
      prev_childs.push({
        prev_prop: p_child.prop,
        prev_el: document.getElementById(p_child.prop),
        prev_dycss: prev_dycss,
        colSpan: convertInlineCss2Number(prev_dycss[GRID_COLUMN]) || 1,
        rowSpan: convertInlineCss2Number(prev_dycss[GRID_ROW]) || 1
      })
    }
  }

  // 2. 获取父容器的布局参数
  const p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
  const gridColumnSize = parseInt(p_dycss[GRID_TEMPLATE_COLUMNS] || "1")
  const gridRowSize = parseInt(p_dycss[GRID_TEMPLATE_ROWS] || "1")
  const p_el_top = convertInlineCss2Number(p_el.style.top)
  const p_el_paddingTop = convertInlineCss2Number(p_dycss[PADDING_TOP])
  const re_row_gap_px = p_el.style.rowGap ? convertInlineCss2Number(p_el.style.rowGap) : 0
  const p_el_height_svg = ((convertInlineCss2Number(p_el.style.height) - (gridRowSize - 1) * re_row_gap_px) - p_el_paddingTop) / gridRowSize

  // 3. 按 Grid 规则逐行填充，记录目标组件的行索引
  let gridRowArray = []
  let currentRow = 0, currentCol = 0
  let targetRowIndex = -1

  for (const child of prev_childs) {
    const childColSpan = child.colSpan || 1
    if (currentRow >= gridRowSize) break

    // 剩余列不足则换行
    if (gridColumnSize - currentCol < childColSpan) {
      currentRow++
      currentCol = 0
      if (currentRow >= gridRowSize) break
    }

    if (!gridRowArray[currentRow]) gridRowArray[currentRow] = []
    gridRowArray[currentRow].push(child)

    if (child.prev_prop === s_comp.prop) targetRowIndex = currentRow
    currentCol += childColSpan
  }

  // 4. 计算目标组件所在行之前的累积高度
  const calcAccumulatedHeight = (rowIndex) => {
    let height = 0
    for (let i = 0; i < rowIndex; i++) {
      const rows = gridRowArray[i]
      if (!rows) {
        height += p_el_height_svg + re_row_gap_px
        continue
      }
      const maxRowSpan = Math.max(...rows.map(r => r.rowSpan || 1))
      height += p_el_height_svg * maxRowSpan + (maxRowSpan - 1) * re_row_gap_px + re_row_gap_px
    }
    return height
  }

  // 5. 处理三种情况，计算最终 top 值
  let result = 0
  const s_mt = convertInlineCss2Number(s_dycss[MARGIN_TOP])

  if (gridRowArray.length === 0) {
    // 情况1：没有兄弟节点
    result = p_el_top + p_el_paddingTop + s_mt
  } else if (targetRowIndex === -1) {
    // 情况2：目标组件被过滤（如隐藏），通过原始列表计算行索引
    const allChildren = components.find(c => c.prop === s_comp.parent).children
    let targetIndex = allChildren.findIndex(c => c.prop === s_comp.prop)
    if (targetIndex !== -1) {
      let tempRow = 0, tempCol = 0
      for (let i = 0; i <= targetIndex && tempRow < gridRowSize; i++) {
        const childComp = components.find(c => c.prop === allChildren[i].prop)
        if (!childComp) continue
        const childDycss = parseToCssDefault(childComp)
        if (childDycss[DISPLAY] === 'none') continue
        const colSpan = convertInlineCss2Number(childDycss[GRID_COLUMN]) || 1
        if (gridColumnSize - tempCol < colSpan) {
          tempRow++
          tempCol = 0
        }
        if (i === targetIndex) targetRowIndex = tempRow
        tempCol += colSpan
      }
    }
    targetRowIndex = targetRowIndex === -1 ? 0 : targetRowIndex
    result = p_el_top + p_el_paddingTop + calcAccumulatedHeight(targetRowIndex) + s_mt
  } else {
    // 情况3：正常找到目标组件
    result = p_el_top + p_el_paddingTop + calcAccumulatedHeight(targetRowIndex) + s_mt
  }

  // 6. 应用到 DOM
  const s_el = document.getElementById(s_comp.prop)
  if (s_el) s_el.style.setProperty(TOP, result + 'px')

  return result
}
// 处理grid布局的left
const calculateGridLeft = (s_comp, s_dycss, p_dycss, components) => {
  // 查找父组件的所有子组件
  const p_childs = components.find(c => c.prop === s_comp.parent).children
  // 筛选所有符合的子组件
  let prev_childs = []
  for (const p_child of p_childs) {
    const prev_comp = components.find(c => c.prop === p_child.prop);
    const prev_dycss = parseToCssDefault(prev_comp)
    if (prev_dycss[DISPLAY] === 'none') {
      continue
    }
    if ((!prev_dycss[POSITION] || prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'relative' || prev_dycss[POSITION] == 'sticky')
      || (p_dycss[POSITION] == "fixed" && prev_dycss[POSITION] == 'relative')
      || (p_dycss[POSITION] == "sticky" && prev_dycss[POSITION] == 'fixed')
      || (p_dycss[POSITION] == "relative" && prev_dycss[POSITION] == 'fixed')
      || (p_dycss[POSITION] == "static" && prev_dycss[POSITION] == 'fixed')
      || (p_dycss[POSITION] == "absolute" && prev_dycss[POSITION] == 'sticky')) {
      prev_childs.push({
        prev_prop: p_child.prop,
        prev_el: document.getElementById(p_child.prop),
        prev_dycss: prev_dycss,
        prev_comp: prev_comp,
        colSpan: convertInlineCss2Number(prev_dycss[GRID_COLUMN]) || 1
      })
    }
  }
  let p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)

  const gridTemplateColumns = p_dycss[GRID_TEMPLATE_COLUMNS]
  const gridColumnSize = parseInt((gridTemplateColumns ? gridTemplateColumns : "1")) // 没有配置列，默认1列

  // 获取 grid 行数
  const gridTemplateRows = p_dycss[GRID_TEMPLATE_ROWS]
  const gridRowSize = parseInt((gridTemplateRows ? gridTemplateRows : "1"))

  const re_column_gap_px = p_el.style.columnGap ? convertInlineCss2Number(p_el.style.columnGap) : 0

  const p_el_paddingLeft = convertInlineCss2Number(p_dycss[PADDING_LEFT])
  const p_el_paddingRight = convertInlineCss2Number(p_dycss[PADDING_RIGHT])

  // 均分grid网格的宽度
  const p_el_width = convertInlineCss2Number(p_el.style.width)
  const totalGap = (gridColumnSize - 1) * re_column_gap_px
  const availableWidth = p_el_width - p_el_paddingLeft - p_el_paddingRight - totalGap
  const cellWidth = availableWidth / gridColumnSize

  let re_left = convertInlineCss2Number(s_dycss[LEFT])

  // 获取当前组件的跨列数
  let colSpan = convertInlineCss2Number(s_dycss[GRID_COLUMN]) || 1

  // 使用二维数组按行列填充，处理跨列换行
  let gridRowArray = []
  let currentRow = 0
  let currentCol = 0

  for (const child of prev_childs) {
    const childColSpan = child.colSpan || 1

    // ⭐ 如果行数已经达到最大限制，跳出（不再放置更多元素）
    if (currentRow >= gridRowSize) {
      break
    }

    // ⭐ 检查当前行剩余列数是否足够
    const remainingCols = gridColumnSize - currentCol
    if (remainingCols < childColSpan) {
      // 不够，换到下一行
      currentRow++
      currentCol = 0
      // ⭐ 换行后再次检查行数是否超出限制
      if (currentRow >= gridRowSize) {
        break
      }
    }
    // 确保行存在
    if (!gridRowArray[currentRow]) {
      gridRowArray[currentRow] = []
    }
    // 记录该元素在行中的列位置
    child._colIndex = currentCol
    gridRowArray[currentRow].push(child)

    // 更新列位置（累加当前元素占用的列数）
    currentCol += childColSpan
  }
  // 循环处理grid的每行数据
  for (const gridRows of gridRowArray) {
    if (!gridRows) continue

    for (const prev_child of gridRows) {
      let calculate_left = 0
      const {prev_el, prev_dycss, prev_prop, _colIndex} = prev_child

      // 当前元素的left
      if (prev_prop == s_comp.prop) {
        const prev_ml = convertInlineCss2Number(prev_dycss[MARGIN_LEFT])
        const prev_lf = convertInlineCss2Number(prev_dycss[LEFT])
        let prev_w = convertInlineCss2Number(prev_dycss[WIDTH])
        const prev_dycss_pl = convertInlineCss2Number(prev_dycss[PADDING_LEFT])
        prev_w = Math.max(prev_w, prev_dycss_pl)
        // 计算列偏移：使用 _colIndex 记录的位置
        const colOffset = p_el_paddingLeft + _colIndex * (cellWidth + re_column_gap_px)
        // 计算跨列元素占据的总宽度
        const elementTotalWidth = cellWidth * colSpan + (colSpan - 1) * re_column_gap_px
        if (!prev_dycss[POSITION]
          || (p_dycss[POSITION] == "fixed" && prev_dycss[POSITION] == 'relative')
          || (p_dycss[POSITION] == "fixed" && prev_dycss[POSITION] == 'sticky')
          || (p_dycss[POSITION] == "sticky" && prev_dycss[POSITION] == 'static')
          || (p_dycss[POSITION] == "relative" && prev_dycss[POSITION] == 'relative')
          || (p_dycss[POSITION] == "absolute" && prev_dycss[POSITION] == 'relative')
          || (p_dycss[POSITION] == "sticky" && prev_dycss[POSITION] == 'relative')
          || (p_dycss[POSITION] == "sticky" && prev_dycss[POSITION] == 'sticky')
          || (p_dycss[POSITION] == "relative" && prev_dycss[POSITION] == 'static')
          || (p_dycss[POSITION] == "relative" && prev_dycss[POSITION] == 'sticky')
          || (p_dycss[POSITION] == "relative" && prev_dycss[POSITION] == 'sticky')
          || (p_dycss[POSITION] == "static" && prev_dycss[POSITION] == 'sticky')
          || (p_dycss[POSITION] == "static" && prev_dycss[POSITION] == 'static')
          || (p_dycss[POSITION] == "static" && prev_dycss[POSITION] == 'relative')
          || (p_dycss[POSITION] == "absolute" && prev_dycss[POSITION] == 'sticky')
          || (p_dycss[POSITION] == "absolute" && prev_dycss[POSITION] == 'static')) {
          // 对齐方式是居中
          if (p_dycss[GRID_JUSTIFY_ITEMS] == "cent er" || prev_dycss[GRID_JUSTIFY_SELF] == "center") {
            calculate_left = colOffset + (elementTotalWidth - prev_w) / 2
            const prev_dycss_ml = convertInlineCss2Number(prev_dycss[MARGIN_LEFT])
            if (prev_dycss_ml > 0) {
              calculate_left -= prev_dycss_ml / 2  // 居中要均分margin_left，外层加了整个值，内部先减半外层在加回去
            }
          } else {
            calculate_left = colOffset + prev_ml
          }

          if (['relative', 'fixed'].includes(prev_dycss[POSITION])) {
            calculate_left += prev_lf
          }

          prev_el.style.setProperty(LEFT, calculate_left + 'px')
        }
        re_left = calculate_left
      }
    }
  }
  return re_left
}

const calculateGridWidth = (s_comp, s_dycss, p_dycss, components) => {
  // 查找父组件的所有子组件
  const p_childs = components.find(c => c.prop === s_comp.parent).children
  // 筛选所有符合的子组件
  let prev_childs = []
  for (const p_child of p_childs) {
    const prev_comp = components.find(c => c.prop === p_child.prop);
    const prev_dycss = parseToCssDefault(prev_comp)
    if (prev_dycss[DISPLAY] === 'none') {
      continue
    }
    if (!prev_dycss[POSITION] || prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'relative' || prev_dycss[POSITION] == 'sticky') {
      // 没有设置宽度，或者宽度是full的 需要才需要计算
      if (!prev_dycss[WIDTH] || prev_dycss[WIDTH] == 'full' || prev_dycss[WIDTH] == 'auto' || prev_dycss[WIDTH] == '100%')
        prev_childs.push({
          prev_prop: p_child.prop,
          prev_el: document.getElementById(p_child.prop),
          prev_dycss: prev_dycss,
          prev_comp: prev_comp
        })
    }
  }
  let p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
  const s_left = s_dycss[LEFT]

  const gridTemplateColumns = p_dycss[GRID_TEMPLATE_COLUMNS]
  const gridColumnSize = parseInt((gridTemplateColumns ? gridTemplateColumns : "1")) // 没有配置列，默认1列

  const re_column_gap_px = p_el.style.columnGap ? convertInlineCss2Number(p_el.style.columnGap) : 0

  // 均分宽度
  const p_el_paddingLeft = convertInlineCss2Number(p_dycss[PADDING_LEFT])
  let p_el_width_svg = ((convertInlineCss2Number(p_el.style.width) - (gridColumnSize - 1) * re_column_gap_px) - p_el_paddingLeft) / gridColumnSize
  // 把grid数据按行数切分为二维数组
  const gridRowArray = [];
  for (let i = 0; i < prev_childs.length; i += gridColumnSize) {
    gridRowArray.push(prev_childs.slice(i, i + gridColumnSize));
  }

  let re_width = convertInlineCss2Number(s_dycss[WIDTH])

  // 循环处理grid的每行数据(每行是一个数组)
  for (const gridRows of gridRowArray) {
    for (const prev_child of gridRows) {
      const {prev_prop, prev_el, prev_dycss} = prev_child
      if (prev_dycss[POSITION] != 'relative' || prev_dycss[POSITION] == 'sticky' || convertInlineCss2Number(s_left) == 0) {
        prev_el.style.setProperty(WIDTH, p_el_width_svg + 'px')
      }
      if (prev_prop == s_comp.prop) {
        re_width = p_el_width_svg
      }
    }
  }
  const prev_el_paddingLeft = convertInlineCss2Number(s_dycss[PADDING_LEFT])
  // 高度计算：grid格子的高度 和 padding-top的宽度取最大值
  re_width = Math.max(re_width, prev_el_paddingLeft);
  return re_width;
}

/**
 * 处理grid的高
 * @param s_comp
 * @param s_dycss
 * @param p_dycss
 * @param components
 */
const calculateGridHeight = (s_comp, s_dycss, p_dycss, components) => {
  // 高度处理
  const s_h = s_dycss[HEIGHT]
  const s_minh = s_dycss[MIN_HEIGHT]
// ast的高度的取值顺序，无有效值则不处理
  let height_ast
  if (s_h) {
    height_ast = s_h
  } else if (s_minh) {
    height_ast = s_minh
  }
  let p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
  // 均分宽度
  const gridTemplateRows = p_dycss[GRID_TEMPLATE_ROWS]
  const gridRowSize = parseInt((gridTemplateRows ? gridTemplateRows : "1")) // 没有配置行，默认1行
  // 父的top
  const p_el_paddingTop = convertInlineCss2Number(p_dycss[PADDING_TOP])

  // const row_gap = p_dycss[ROW_GAP] // 行间距
  // const re_row_gap_px =parseInt((row_gap ? row_gap : "0"))  * 4 // 间距换成px
  // const s_el = document.getElementById(s_comp.prop)
  const re_row_gap_px = p_el.style.rowGap ? convertInlineCss2Number(p_el.style.rowGap) : 0

  // 均分高度【公式：((父的height-(grid行大小-1) * 行间距) - 父paddingTop) / 行大小 】
  let p_el_height_svg = ((convertInlineCss2Number(p_el.style.height) - (gridRowSize - 1) * re_row_gap_px) - p_el_paddingTop) / gridRowSize;
  if (height_ast && !["screen", "auto", "full"].includes(height_ast)) {
    p_el_height_svg = convertInlineCss2Number(s_dycss[HEIGHT])
  } else if (!height_ast) {
    p_el_height_svg = calculateDefaultHeight(s_dycss, s_comp, components)
  }
  const s_el_paddingTop = convertInlineCss2Number(s_dycss[PADDING_TOP])
  // 高度计算：grid格子的高度 和 padding-top的宽度取最大值
  return Math.max(p_el_height_svg, s_el_paddingTop);
}

/**
 * 计算static原始坐标
 * @param s_comp
 * @param comps
 */
const calculateDefaultStaticTop = (s_comp, comps) => {
  let p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
  const p_el_top = convertInlineCss2Number(p_el.style.top)
  const p_dycss = parseToCssDefault(comps.find(c => c.prop === s_comp.parent))
  const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
  // 查找父组件的所有子组件
  const p_childs = comps.find(c => c.prop === s_comp.parent).children
  // 筛选所有符合的子组件
  let prev_childs = []
  for (const p_child of p_childs) {
    const prev_comp = comps.find(c => c.prop === p_child.prop);
    const prev_dycss = parseToCssDefault(prev_comp)
    // ⭐ 跳过隐藏节点
    if (prev_dycss[DISPLAY] === 'none') {
      continue
    }
    if (prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'relative' || prev_dycss[POSITION] == 'sticky') {
      prev_childs.push({
        prev_prop: p_child.prop,
        prev_el: document.getElementById(p_child.prop),
        prev_dycss: prev_dycss,
        prev_comp: prev_comp
      })
    }
  }
  let prev_mb_next = 0, prev_h_next = 0, prev_mb_acc = 0;
  let result = null;
  for (const prev_child of prev_childs) {
    const {prev_el, prev_dycss, prev_comp} = prev_child
    const prev_mt = convertInlineCss2Number(prev_dycss[MARGIN_TOP])
    const prev_mb = convertInlineCss2Number(prev_dycss[MARGIN_BOTTOM])
    const prev_h = convertInlineCss2Number(calculateHeight(prev_dycss, prev_comp, comps))
    // 上一个的下边距，与当前的上边距，取最大值
    const prev_mt_max = findMax([prev_mb_next, prev_mt])
    let prev_value = p_el_top + p_pt + prev_h_next + prev_mt_max + prev_mb_acc
    if (prev_dycss[POSITION] == 'sticky') {
      prev_value += convertInlineCss2Number(prev_dycss[TOP])
    }
    // relative只计算，不参与赋值
    if (!prev_dycss[POSITION] || prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'sticky') {
      prev_el.style.setProperty(TOP, prev_value + 'px')
      if (prev_child.prev_prop == s_comp.prop) {  // 返回当前节点 s_comp 的 prop
        result = prev_value;
      }
    }
    // 将计算得到的间距累加，给下一个计算
    prev_mb_acc += prev_mt_max
    // 将高度累加，给下一个计算
    prev_h_next += prev_h
    // 将当前的下边距，给下一个计算
    prev_mb_next = prev_mb
  }
  return result;
}

/**
 * 计算relate原始坐标，top=0，left=0
 * @param s_comp
 * @param comps
 */
const calculateDefaultRelativeTop = (s_comp, comps) => {
  // relative需要计算真实位置
  const p_comp = comps.find(c => c.prop === s_comp.parent);
  const p_dycss = parseToCssDefault(p_comp)
  const p_el_top = convertInlineCss2Number(p_dycss[MARGIN_TOP])
  const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
  // 查找父组件的所有子组件
  const p_childs = p_comp.children
  //  过滤隐藏的子节点，只保留目标节点之前的可见节点
  const prev_childs = []
  for (const p_child of p_childs) {
    const prev_comp = comps.find(c => c.prop === p_child.prop);
    const prev_dycss = parseToCssDefault(prev_comp)
    // ⭐ 跳过隐藏节点
    if (prev_dycss[DISPLAY] === 'none') {
      continue
    }
    if (prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'relative' || prev_dycss[POSITION] == 'sticky') {
      prev_childs.push({prev_dycss: prev_dycss, prev_comp: prev_comp})
    }
    if (p_child.prop == s_comp.prop) {
      break
    }
  }
  let prev_h_next = 0, prev_top = p_el_top + p_pt
  for (const prev_child of prev_childs) {
    const {prev_dycss, prev_comp} = prev_child
    const prev_mt = convertInlineCss2Number(prev_dycss[MARGIN_TOP])
    const prev_h = convertInlineCss2Number(calculateHeight(prev_dycss, prev_comp, comps))
    // 上一个的下边距，与当前的上边距，取最大值
    prev_top += prev_h_next + prev_mt
    // 将高度累加，给下一个计算
    prev_h_next = prev_h
  }
  return prev_top
}

/**
 * 计算高度
 * @param s_dycss
 * @param s_comp
 * @param components
 */
const calculateHeight = (s_dycss, s_comp, components) => {
  const p_comp = components.find(c => c.prop === s_comp.parent)
  const p_dycss = parseToCssDefault(p_comp)
  // 给默认值，无高给0
  let value = 0
  if ('grid' == p_dycss[DISPLAY] && !["absolute", "fixed"].includes(s_dycss[POSITION])) { // 处理节点的定位是 绝对布局absolute 和 冻结fixed，父节点是grid布局
    // grid高度处理
    value = calculateGridHeight(s_comp, s_dycss, p_dycss, components)
  } else {
    // 默认高度处理
    value = calculateDefaultHeight(s_dycss, s_comp, components)
  }
  return value + 'px'
}

/**
 * 计算默认的高度
 * @param s_dycss
 * @param s_comp
 * @param comps
 */
const calculateDefaultHeight = (s_dycss, s_comp, comps) => {
  // 高度处理
  const s_h = s_dycss[HEIGHT]
  const s_minh = s_dycss[MIN_HEIGHT]
// ast的高度的取值顺序，无有效值则不处理
  let astValue
  if (s_h) {
    astValue = s_h
  } else if (s_minh) {
    astValue = s_minh
  }
  // 给默认值，无高给0
  let value = 0
  const isHorizontalLayout = isParentHorizontalLayout(s_dycss)
  if (astValue == 'auto') {
    // 自动计算，按子节点的static和relative的计算
    // 筛选所有符合的子组件
    let prev_childs = []
    // 查找父组件的所有子组件
    for (const p_child of s_comp.children) {
      const prev_comp = comps.find(c => c.prop === p_child.prop);
      const prev_dycss = parseToCssDefault(prev_comp)
      if (prev_dycss[DISPLAY] === 'none') {
        continue
      }
      if (prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'relative' || prev_dycss[POSITION] == 'sticky') {
        prev_childs.push({prev_dycss: prev_dycss, prev_comp: prev_comp})
      }
    }
    if (isHorizontalLayout) {
      let maxHeight = 0
      for (const prev_child of prev_childs) {
        const {prev_dycss, prev_comp} = prev_child
        console.log("1551=======================================", prev_dycss, prev_comp)
        const prev_h = convertInlineCss2Number(calculateHeight(prev_dycss, prev_comp, comps))
        const prev_mt = convertInlineCss2Number(prev_dycss[MARGIN_TOP])
        const prev_mb = convertInlineCss2Number(prev_dycss[MARGIN_BOTTOM])
        // 单个子元素的总高度 = 自身高度 + margin
        const totalHeight = prev_h + prev_mt + prev_mb
        maxHeight = Math.max(maxHeight, totalHeight)
        console.log("max------------------------", maxHeight)
      }
      value = maxHeight + convertInlineCss2Number(s_dycss[PADDING_TOP]) + convertInlineCss2Number(s_dycss[PADDING_BOTTOM])
    } else {
      let prev_mb_next = 0, prev_mb_acc = 0, i = 0, c_h = 0
      for (const prev_child of prev_childs) {
        const {prev_dycss, prev_comp} = prev_child
        const prev_mt = convertInlineCss2Number(prev_dycss[MARGIN_TOP])
        const prev_mb = convertInlineCss2Number(prev_dycss[MARGIN_BOTTOM])
        const prev_h = convertInlineCss2Number(calculateHeight(prev_dycss, prev_comp, comps))
        // 上一个的下边距，与当前的上边距，取最大值
        const prev_mt_max = findMax([prev_mb_next, prev_mt])
        c_h += prev_h + prev_mt_max + prev_mb_acc
        // 将计算得到的间距累加，给下一个计算
        prev_mb_acc += prev_mt_max
        // 将当前的下边距，给下一个计算
        prev_mb_next = prev_mb
        if (i == _size(prev_childs) - 1) {
          // 最后一个儿子加上mb
          c_h += prev_mb_next
        }
        i++
      }
      if (c_h != 0) {
        value = c_h + convertInlineCss2Number(s_dycss[PADDING_TOP]) + convertInlineCss2Number(s_dycss[PADDING_BOTTOM])
      } else {
        value = 0
      }
    }
  } else if (astValue == 'full' || astValue == 'screen') {
    let p_comp, p_el
    if (!s_dycss[POSITION] || s_dycss[POSITION] == 'static' || s_dycss[POSITION] == 'relative') {
      p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
    } else if (s_dycss[POSITION] == 'fixed') {
      p_el = document.getElementById(DESIGNBOARD)
    } else if (s_dycss[POSITION] == 'absolute') {
      p_comp = findParentCompWithSpecificPosi(s_comp.prop);
      p_el = document.getElementById(p_comp.prop === 'root' ? DESIGNBOARD : p_comp.prop)
    }
    value = convertInlineCss2Number(p_el.style.height)
  } else if (isValidPositiveFraction(astValue)) {
    // 分数
    let p_comp, p_el
    if (!s_dycss[POSITION] || s_dycss[POSITION] == 'static' || s_dycss[POSITION] == 'relative') {
      p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
    } else if (s_dycss[POSITION] == 'fixed') {
      p_el = document.getElementById(DESIGNBOARD)
    } else if (s_dycss[POSITION] == 'absolute') {
      p_comp = findParentCompWithSpecificPosi(s_comp.prop);
      p_el = document.getElementById(p_comp.prop === 'root' ? DESIGNBOARD : p_comp.prop)
    }
    const p_el_h = convertInlineCss2Number(p_el.style.height)
    value = calculateFraction(astValue) * p_el_h
  } else if (isPercentage(astValue)) {
    let p_comp, p_el
    if (!s_dycss[POSITION] || s_dycss[POSITION] == 'static' || s_dycss[POSITION] == 'relative') {
      p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
    } else if (s_dycss[POSITION] == 'fixed') {
      p_el = document.getElementById(DESIGNBOARD)
    } else if (s_dycss[POSITION] == 'absolute') {
      p_comp = findParentCompWithSpecificPosi(s_comp.prop);
      p_el = document.getElementById(p_comp.prop === 'root' ? DESIGNBOARD : p_comp.prop)
    }
    const p_el_h = convertInlineCss2Number(p_el.style.height)
    // 百分比
    value = calculatePercentage(astValue) * p_el_h
  } else if (astValue) {
    // 其他场景，直接使用ast值
    value = convertInlineCss2Number(astValue)
  } else if (!astValue && _size(s_comp.children) > 0) {
    for (const child of s_comp.children) {
      const c_comp = comps.find(c => c.prop === child.prop);
      const c_dycss = parseToCssDefault(c_comp)
      value += convertInlineCss2Number(calculateHeight(c_dycss, c_comp, comps))
    }
  } else {
    // 无高度值处理
    const s_el = document.getElementById(s_comp.prop)
    // 移除高度
    s_el.style.height = ''
    value = document.getElementById(s_comp.prop).offsetHeight
  }
  return value
}


const isGridMultiColumn = (columns) => {
  if (!columns) return false
  const str = columns.trim()
  // 纯数字 > 1 或 repeat(数字) > 1 或 空格数量 >= 1
  return /^\d+$/.test(str) ? parseInt(str) > 1
    : str.includes('repeat') ? parseInt(str.match(/repeat\((\d+)/)?.[1] || 1) > 1
      : str.split(/\s+/).length > 1
}

/**
 * 判断当前节点的子节点是水平还是垂直排列
 * @param {Object} s_dycss - 当前节点的样式
 * @returns {boolean} true=水平排列，false=垂直排列
 */
const isParentHorizontalLayout = (s_dycss) => {
  const display = s_dycss?.[DISPLAY]
  if (!display) return false

  if (display === 'flex' || display === 'inline-flex') {
    return s_dycss[FLEX_DIRECTION] !== 'col' && s_dycss[FLEX_DIRECTION] !== 'col-reverse'
  }

  if (display === 'grid' || display === 'inline-grid') {
    return isGridMultiColumn(s_dycss[GRID_TEMPLATE_COLUMNS])
  }

  return false
}

/**
 * 计算宽度
 * @param s_dycss
 * @param s_comp
 * @param components
 */
const calculateWidth = (s_dycss, s_comp, components) => {

  const p_comp = components.find(c => c.prop === s_comp.parent)
  const p_dycss = parseToCssDefault(p_comp)
  // 给默认值
  let value = 0
  // ast的宽度的取值顺序，无有效值则不处理

  if ("grid" == p_dycss[DISPLAY] && !["absolute", "fixed"].includes(s_dycss[POSITION])) {  // 处理节点的定位是 绝对布局absolute 和 冻结fixed，父节点是grid布局
    value = calculateGridWidth(s_comp, s_dycss, p_dycss, components)
  } else { // 普通布局的处理
    value = calculatedefaultWidth(s_dycss, s_comp, components)
  }

  return value + 'px'
}

/**
 * 计算默认宽度
 * @param s_dycss
 * @param s_comp
 * @param comps
 */
const calculatedefaultWidth = (s_dycss, s_comp, comps) => {
  // 宽度处理
  let s_w = s_dycss[WIDTH]
  let s_minw = s_dycss[MIN_WIDTH]
  let s_maxw = s_dycss[MAX_WIDTH]

  // 获取实际值（未设置时为默认值）
  s_minw = s_minw ? s_minw : 0; // 默认值 = 0
  s_w = s_w || s_w == 0 ? s_w : "auto"; // 默认值 = auto
  s_maxw = s_maxw || s_maxw == 0 ? s_maxw : "none"; // 默认值 = none

  // 规则1：min-height 始终是强制下限
  // 规则2：max-height 是强制上限（除非与 min-height 冲突）
  // 规则3：height 是理想值（受限于 min/max）
  // 特殊情况：min-height > max-height 且 maxH 有效时
  if (s_maxw !== "none" && s_minw > s_maxw) {
    return convertInlineCss2Number(s_minw); // min-height 优先级最高
  }
  const p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
  // 父容器总宽度
  let p_el_w = convertInlineCss2Number(p_el.style.width)
  // 宽度处理
  const s_ml = convertInlineCss2Number(s_dycss[MARGIN_LEFT])
  const s_mr = convertInlineCss2Number(s_dycss[MARGIN_RIGHT])
  // 给默认值
  let value = 0
  // 宽度默认父组件宽
  const p_comp = comps.find(c => c.prop === s_comp.parent)
  const p_dycss = parseToCssDefault(p_comp)
  if (s_w) {
    // 其他场景处理，验证是否为严格意义上的正分数（必须包含分数线，分子分母都是正整数，分子不为0）
    if (isValidPositiveFraction(s_w)) {
      // 分数关于父组件，计算字符串的分数数值
      value = calculateFraction(s_w) * p_el_w
    } else if (s_w == 'full' || s_w == '100%' || s_w == 'screen') {
      // absolute的位置是最上非static，递归往上找，返回 position 不为 static 的父组件（absolute 是相对于不为 static 的偏移）
      const p_comp_not_static = findParentCompWithSpecificPosi(s_comp.prop);
      const p_dycss_not_static = parseToCssDefault(p_comp_not_static)
      if (p_dycss[POSITION] == 'static' && ["fixed", "absolute"].includes(s_dycss[POSITION])) {
        value = convertInlineCss2Number(p_dycss_not_static[WIDTH])
        // 根据优先级查询宽度，findWinthByPriority(width, max_width, min_width) 判断是用 宽度？最大宽度？最小宽度？
        value = findWinthByPriority(value, s_maxw, s_minw)
      } else if (s_dycss[POSITION] == 'fixed') {
        const p_el = document.getElementById(DESIGNBOARD)
        value = convertInlineCss2Number(p_el.style.width)
        // 根据优先级查询宽度，findWinthByPriority(width, max_width, min_width) 判断是用 宽度？最大宽度？最小宽度？
        value = findWinthByPriority(value, s_maxw, s_minw)
      } else {
        // 根据优先级查询宽度，findWinthByPriority(width, max_width, min_width) 判断是用 宽度？最大宽度？最小宽度？
        let p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
        let p_pr = convertInlineCss2Number(p_dycss[PADDING_RIGHT])
        if (p_pl === 0 || p_pr === 0) {
          const {left, right} = parsePadding(p_dycss[PADDING])
          p_pl = left || 0
          p_pr = right || 0
        }
        p_el_w = p_el_w - p_pl - p_pr - s_ml - s_mr
        console.log("1. p_el_w 初始值:", p_el_w)
        console.log("2. p_pl:", p_pl)
        console.log("3. p_pr:", p_pr)
        console.log("4. s_ml:", s_ml)
        console.log("5. s_mr:", s_mr)
        value = findWinthByPriority(p_el_w, s_maxw, s_minw)
      }
    } else if (s_w == 'auto') {
      let p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
      let p_pr = convertInlineCss2Number(p_dycss[PADDING_RIGHT])
      if (p_pl === 0 || p_pr === 0) {
        const {left, right} = parsePadding(p_dycss[PADDING])
        p_pl = left || 0
        p_pr = right || 0
      }
      if (!p_dycss[POSITION] || p_dycss[POSITION] == 'static' || p_dycss[POSITION] == 'relative' || p_dycss[POSITION] == 'sticky') {
        value = p_el_w - p_pl - p_pr - s_ml - s_mr
      } else if (p_dycss[POSITION] == 'absolute') {
        // 自动计算，按子节点的static和relative的计算
        // 将子节点进行分组
        // 过滤出 static，relative，sticky
        let prev_childs = []
        for (const {prop: prev_prop} of s_comp.children) {
          const prev_comp = comps.find(c => c.prop === prev_prop)
          const prev_dycss = parseToCssDefault(prev_comp)
          if (prev_dycss[DISPLAY] === 'none') {
            continue
          }
          if (!prev_dycss[POSITION] || prev_dycss[POSITION] == 'static' || prev_dycss[POSITION] == 'relative' || prev_dycss[POSITION] == 'sticky') {
            prev_childs.push({prev_comp: prev_comp, prev_dycss: prev_dycss})
          }
        }
        value = 0
        if (prev_childs.length > 0) {
          // 有子节点：累加子节点宽度
          for (const {prev_comp, prev_dycss} of prev_childs) {
            const prev_ml = convertInlineCss2Number(prev_dycss[MARGIN_LEFT])
            const prev_mr = convertInlineCss2Number(prev_dycss[MARGIN_RIGHT])
            const prev_w = convertInlineCss2Number(calculateWidth(prev_dycss, prev_comp, comps))
            value += prev_ml + prev_mr + prev_w
          }
        } else {
          // 没有子节点：使用父容器可用宽度
          // 默认使用父容器可用宽度
          value = p_el_w - p_pl - p_pr - s_ml - s_mr
        }

      } else {
        value = 0
      }
      // 根据优先级查询宽度
      value = findWinthByPriority(value, s_maxw, s_minw)
    } else {
      // 不存在任何宽度，默认父级
      // const w = convertInlineCss2Number(astValue)
      // value = w!=0?w:convertInlineCss2Number(p_el_w)
      // 根据优先级查询宽度
      value = findWinthByPriority(s_w, s_maxw, s_minw)
    }
  } else {
    const prev_dycss = s_comp.dynamicCss[0].sources[0].css.normal
    if (!["absolute", "sticky"].includes(prev_dycss[POSITION])) {
      // 无宽度默认父级
      value = convertInlineCss2Number(p_el_w)
    }
  }
  return value
}


/**
 * 解析 padding 值，支持标准的 CSS padding 简写格式
 * @param {string|Object} paddingValue - padding 值
 * @returns {{top: number, right: number, bottom: number, left: number}} 各方向的 padding 值（像素）
 *
 * 支持格式：
 * 1. 单个值：'10px' → 所有方向 10px
 * 2. 两个值：'10px 20px' → 上下 10px，左右 20px
 * 3. 三个值：'10px 20px 30px' → 上 10px，左右 20px，下 30px
 * 4. 四个值：'10px 20px 30px 40px' → 上 10px，右 20px，下 30px，左 40px
 */
const parsePadding = (paddingValue) => {
  let top = 0, right = 0, bottom = 0, left = 0

  // 如果没有值，直接返回 0
  if (!paddingValue) {
    return {top: 0, right: 0, bottom: 0, left: 0}
  }

  // 如果是数字
  if (typeof paddingValue === 'number') {
    top = right = bottom = left = paddingValue
    return {top, right, bottom, left}
  }

  // 如果是字符串
  if (typeof paddingValue === 'string' && paddingValue.trim()) {
    const parts = paddingValue.trim().split(/\s+/).filter(p => p !== '')

    if (parts.length === 1) {
      const val = convertInlineCss2Number(parts[0])
      top = right = bottom = left = val || 0
    } else if (parts.length === 2) {
      top = bottom = convertInlineCss2Number(parts[0]) || 0
      right = left = convertInlineCss2Number(parts[1]) || 0
    } else if (parts.length === 3) {
      top = convertInlineCss2Number(parts[0]) || 0
      right = left = convertInlineCss2Number(parts[1]) || 0
      bottom = convertInlineCss2Number(parts[2]) || 0
    } else if (parts.length >= 4) {
      top = convertInlineCss2Number(parts[0]) || 0
      right = convertInlineCss2Number(parts[1]) || 0
      bottom = convertInlineCss2Number(parts[2]) || 0
      left = convertInlineCss2Number(parts[3]) || 0
    }
  }

  return {top, right, bottom, left}
}


/**
 * 根据优先级查询宽度
 * @param width 宽度的值
 * @param max_width 最大宽度
 * @param min_width 最小宽度
 */
function findWinthByPriority(width, max_width, min_width) {
  if (max_width === "none") {
    return Math.max(convertInlineCss2Number(width), convertInlineCss2Number(min_width)); // 仅受 min-width 限制
  } else {
    return Math.max(Math.min(convertInlineCss2Number(width), convertInlineCss2Number(max_width)), convertInlineCss2Number(min_width));
  }
}


/**
 * 查找最大值
 * @param arr
 */
function findMax(arr) {
  // Check if the array is empty
  if (arr.length === 0) {
    return undefined; // Or throw an error, depending on desired behavior for empty arrays
  }

  let max = convertInlineCss2Number(arr[0]); // Assume the first element is the maximum initially

  // Iterate through the rest of the array to find the true maximum
  for (let i = 1; i < arr.length; i++) {
    let x = convertInlineCss2Number(arr[i]);
    if (x > max) {
      max = x;
    }
  }

  return max;
}

/**
 * 取最小值
 * @param arr
 * @returns {*|null}
 */
export function findMinX(arr) {
  // 如果数组为空，返回 null
  if (!arr || arr.length === 0) {
    return null;
  }

  // 初始化最小值对象为数组中第一个对象
  let minPoint = arr[0];

  // 遍历数组中的每个对象
  for (let i = 1; i < arr.length; i++) {
    // 如果当前对象的 x 值小于最小值对象的 x 值，则更新最小值对象
    if (arr[i].x < minPoint.x) {
      minPoint = arr[i];
    }
  }

  return minPoint;
}

/**
 * 计算拖拽初始化值
 * @param prop
 * @param comps
 * @param event
 */
export const calculateDragInit = (prop, comps, event) => {
  // 初始化
  const s_el_top = convertInlineCss2Number(event.target.style.top)
  const s_el_left = convertInlineCss2Number(event.target.style.left)
  const s_el_w = convertInlineCss2Number(event.target.style.width)
  const s_el_h = convertInlineCss2Number(event.target.style.height)
  // 更新AST
  const s_comp = comps.find(c => c.prop === prop)
  const s_dycss = parseToCssDefault(s_comp)
  const s_mt = convertInlineCss2Number(s_dycss[MARGIN_TOP])
  const s_ml = convertInlineCss2Number(s_dycss[MARGIN_LEFT])
  // 查找所有的最近的父组件
  let p_range = []
  for (const comp of comps) {
    if (comp.prop !== s_comp.prop && (comp.type === 'frame' || comp.type === 'cyclic-region' || comp.type === 'group')) {
      const p_dycss = parseToCssDefault(comp);
      const p_el = document.getElementById(comp.prop === 'root' ? DESIGNBOARD : comp.prop)
      const p_el_left = convertInlineCss2Number(p_el.style.left)
      const p_el_top = convertInlineCss2Number(p_el.style.top)
      const p_el_w = convertInlineCss2Number(p_el.style.width)
      const p_el_h = convertInlineCss2Number(p_el.style.height)
      // 范围
      let p_x = p_el_left + p_el_w
      let p_y = p_el_top + p_el_h
      if (s_el_top < p_y && s_el_left < p_x) {
        p_range.push({prop: comp.prop, x: p_x, y: p_y, p_dycss: p_dycss, p_el: p_el})
      }
    }
  }
  let p_mincomp = findMinX(p_range)
  // 将当前组件落到父节点上
  const p_comp = comps.find(c => c.prop === p_mincomp.prop)
  const p_dycss = p_mincomp.p_dycss
  const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
  const p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
  const p_el = p_mincomp.p_el
  const p_el_top = convertInlineCss2Number(p_el.style.top)
  const p_el_left = convertInlineCss2Number(p_el.style.left)

  // 建立父子关系
  p_comp.children.push({prop: s_comp.prop})
  _set(s_comp, 'parent', p_mincomp.prop)
  // 更新层级 层级：root为1，一层加1
  // 取父级z-index
  let p_z = p_mincomp.p_dycss[Z_INDEX]
  if (p_z === 'auto') {
    _set(s_comp, 'dynamicCss[0].sources[0].css.normal.z-index', 'z-auto')
  } else {
    _set(s_comp, 'dynamicCss[0].sources[0].css.normal.z-index', `z-${convertInlineCss2Number(p_z) + 1}`)
  }

  const s_top = s_el_top - p_el_top - p_pt - s_mt
  _set(s_comp, 'dynamicCss[0].sources[0].css.normal.top', parseCssPx2TailwindRem(TOP, s_top))
  const s_left = s_el_left - p_el_left - p_pl - s_ml
  _set(s_comp, 'dynamicCss[0].sources[0].css.normal.left', parseCssPx2TailwindRem(LEFT, s_left))
  _set(s_comp, 'dynamicCss[0].sources[0].css.normal.width', parseCssPx2TailwindRem(WIDTH, s_el_w))
  _set(s_comp, 'dynamicCss[0].sources[0].css.normal.height', parseCssPx2TailwindRem(HEIGHT, s_el_h))
}

/**
 * 计算大小
 * @param prop
 * @param comps
 */
export const calculateResize = (prop, comps) => {
  // 调整大小
  const s_el = document.getElementById(prop);
  const s_el_w = convertInlineCss2Number(s_el.style.width)
  const s_el_h = convertInlineCss2Number(s_el.style.height)
  const s_comp = comps.find(c => c.prop === prop)
  // 更新位置
  // calculateCompMove([prop], comps, false)

  _set(s_comp, 'dynamicCss[0].sources[0].css.normal.width', parseCssPx2TailwindRem(WIDTH, s_el_w))
  _set(s_comp, 'dynamicCss[0].sources[0].css.normal.height', parseCssPx2TailwindRem(HEIGHT, s_el_h))

  let renderProp = getRenderProp(prop, comps);  // 获取要刷新的父节点
  localRecurseCalcCompsXY(renderProp, comps);

  // fixWhitHeight(renderProp, comps, rectArr);
  //
  // for (let i1 = 0; i1 < rectArr.length; i1++) {
  //   const s_el = document.getElementById(rectArr[i1].prop === 'root' ? DESIGNBOARD : rectArr[i1].prop);
  //   s_el.style.setProperty(WIDTH, rectArr[i1].width);
  //   s_el.style.setProperty(HEIGHT, rectArr[i1].height);
  //   s_el.style.setProperty(LEFT, rectArr[i1].left);
  //   s_el.style.setProperty(TOP, rectArr[i1].top);
  // }

  // return rectArr;
}

/**
 * @description 重新找父级
 * @param { string[] } props 组件 id
 * @param comps
 * @param isChild
 * @returns { ComponentItem[] } components 的副本
 */
export const calculateCompMove = (props: string[], comps: ComponentItem[], isChild): ComponentItem[] => {
  for (const prop of props) {
    const s_el = document.getElementById(prop)
    const s_comp = comps.find(c => c.prop === prop)
    if (!(s_comp && s_el)) continue
    // 将子节点位置全部更新
    for (const child of s_comp.children) {
      calculateCompMove([child.prop], comps, true);
    }
    const s_el_left = convertInlineCss2Number(s_el.style.left)
    const s_el_top = convertInlineCss2Number(s_el.style.top)
    const s_dycss = parseToCssDefault(s_comp)
    const s_position = s_dycss[POSITION]
    const s_mt = convertInlineCss2Number(s_dycss[MARGIN_TOP])
    const s_ml = convertInlineCss2Number(s_dycss[MARGIN_LEFT])


    if (s_position == 'fixed' && !isChild) {
      // fixed，可拖动任意位置，不改变父子关系
      _set(s_comp, 'dynamicCss[0].sources[0].css.normal.top', parseCssPx2TailwindRem(TOP, s_el_top))
      _set(s_comp, 'dynamicCss[0].sources[0].css.normal.left', parseCssPx2TailwindRem(LEFT, s_el_left))
    }

    // 计算relative真实位置

    if (s_position == 'absolute') {
      // 可拖动任意位置，不改变父子关系
      // absolute找非static父节点
      const p_comp = findParentCompWithSpecificPosi(s_comp.prop);
      const p_dycss = parseToCssDefault(p_comp)
      const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
      const p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
      const p_el = document.getElementById(p_comp.prop === 'root' ? DESIGNBOARD : p_comp.prop)
      const p_el_top = convertInlineCss2Number(p_el.style.top)
      const p_el_left = convertInlineCss2Number(p_el.style.left)
      const s_top = s_el_top - p_el_top - p_pt - s_mt
      _set(s_comp, 'dynamicCss[0].sources[0].css.normal.top', parseCssPx2TailwindRem(TOP, s_top))
      const s_left = s_el_left - p_el_left - p_pl - s_ml
      _set(s_comp, 'dynamicCss[0].sources[0].css.normal.left', parseCssPx2TailwindRem(LEFT, s_left))
    }

    if (s_position == 'relative') {
      const p_el = document.getElementById(s_comp.parent === 'root' ? DESIGNBOARD : s_comp.parent)
      const p_el_left = convertInlineCss2Number(p_el.style.left)
      let p_comp = comps.find(c => c.prop === s_comp.parent);
      const p_dycss = parseToCssDefault(p_comp)
      const p_pt = convertInlineCss2Number(p_dycss[PADDING_TOP])
      const p_pl = convertInlineCss2Number(p_dycss[PADDING_LEFT])
      // relative获取原始位置
      let re_top = 0, re_left = 0
      if (p_dycss[DISPLAY] == 'flex') {
        re_top = calculateFlexTop(s_dycss, s_comp, p_comp, p_dycss, p_el, comps)
        re_left = calculateFlexLeft(s_dycss, s_comp, p_comp, p_dycss, p_el, comps)
      } else if (p_dycss[DISPLAY] == 'grid') {
        re_top = calculateGridTop(s_comp, s_dycss, p_dycss, comps)
        re_left = calculateGridLeft(s_comp, s_dycss, p_dycss, comps)
      } else {
        re_top = calculateDefaultRelativeTop(s_comp, comps);
        re_left = p_el_left
      }
      const s_top = s_el_top - re_top - p_pt - s_mt
      _set(s_comp, 'dynamicCss[0].sources[0].css.normal.top', parseCssPx2TailwindRem(TOP, s_top))
      const s_left = s_el_left - re_left - p_pl - s_ml
      _set(s_comp, 'dynamicCss[0].sources[0].css.normal.left', parseCssPx2TailwindRem(LEFT, s_left))
    }

    // static/sticky  不参与拖动

  }
  return comps
}

/**
 * 判断结构中是否存在css_default样式
 */
export const parseToCssDefault = (component) => {
  let standardStyle: { [cssKey: string]: any }
  standardStyle = parseTailwindToCss(component.dynamicCss[0].sources[0].css.normal) // 都是带单位的

  return standardStyle
}


/**
 * 将对象中的 Tailwind CSS gap 属性通过标准公式转换为像素值
 * @param {object} styles - 包含 Tailwind gap 类名的样式对象
 * @returns {object} 转换后的样式对象
 */
export const convertGapByFormula = (styles) => {
  const result = {...styles};

  const convertValue = (value) => {
    if (value === 'px') return '1px';

    const num = parseInt(value, 10);
    if (isNaN(num)) return undefined;

    // Tailwind 标准公式: 0.25rem * 数字 * 16(1rem=16px)
    const pxValue = 0.25 * num * 16;
    return `${pxValue}px`;
  };

  // 处理 gap 属性
  if (result.gap) {
    const value = result.gap.match(/(\d+|px)$/)?.[0];
    if (value) result.gap = convertValue(value);
  }

  // 处理 gap-x (columnGap)
  if (result.columnGap) {
    const value = result.columnGap.match(/(\d+|px)$/)?.[0];
    if (value) result.columnGap = convertValue(value);
  }

  // 处理 gap-y (rowGap)
  if (result.rowGap) {
    const value = result.rowGap.match(/(\d+|px)$/)?.[0];
    if (value) result.rowGap = convertValue(value);
  }

  return result;
}


/**
 * 将样式中的 right/bottom 转换为 left/top
 * @param style
 * @param {number} parentWidth - 父容器宽度（可选，默认视口宽度）
 * @param {number} parentHeight - 父容器高度（可选，默认视口高度）
 * @returns {Object} - 转换后的样式对象
 */
export const convertRightBottomToLeftTop = (style, parentWidth, parentHeight) => {
  const result = {...style};

  const parsePx = (value) => {
    if (typeof value === 'string' && value.endsWith('px')) {
      return parseFloat(value);
    }
    if (typeof value === 'number') return value;
    return 0;
  };

  const effectiveParentWidth = parentWidth ?? window.innerWidth;
  const effectiveParentHeight = parentHeight ?? window.innerHeight;

  const elementWidth = parsePx(style.width);
  const elementHeight = parsePx(style.height);

  if ('right' in style) {
    const rightValue = parsePx(style.right);
    result.left = `${effectiveParentWidth - elementWidth - rightValue}px`;
    delete result.right;
  }

  if ('bottom' in style) {
    const bottomValue = parsePx(style.bottom);
    result.top = `${effectiveParentHeight - elementHeight - bottomValue}px`;
    delete result.bottom;
  }

  return result;
};


export const replaceCompTopLeft = () => {
  const components = astStore.state.components;
  for (const data in components) {
    const gap = convertGapByFormula(_get(data, 'dynamicCss[0].sources[0].css.css_default'))
    _set(data, 'dynamicCss[0].sources[0].css.css_default', gap);
    const parent = components.find(item => item.prop === components[data].parent);
    if (parent) {
      const parentStyles = _get(parent, 'dynamicCss[0].sources[0].css.css_default')
      if (_get(data, 'dynamicCss[0].sources[0].css.css_default') && parentStyles) {
        const result = convertRightBottomToLeftTop(parseToCssDefault(data), parentStyles.width, parentStyles.height);
        _set(data, 'dynamicCss[0].sources[0].css.css_default', result);
      }
    }
  }
  updateASTComponents(components);
}


/**
 * @description 处理拖拽后的逻辑
 * @param props 拖拽的节点
 * @param comps AST
 * @param event 事件
 * @param action 拖拽类型
 * @returns
 */
export function handleDrag(props, comps, event, action) {
  handleFixedDrag(props, comps, event);
  // 处理拖拽后的父子关系，返回原父节点、新父节点
  let oldNewParent = handleParentChildren(props, comps, event, action);
  // 处理 flex 布局
  let pComp = comps.find(c => c.prop === oldNewParent[0].newParent);
  if (getLayoutType(pComp) == "flex") {  // 普通布局才需要处理
    handleFlexLayout(props, pComp, comps, action);
  }

  // 处理 grid 布局
  if (getLayoutType(pComp) == "grid") {  // grid布局才需要处理
    handleGridLayout(props, pComp, comps, action);
  }
  // 在递归遍历整个AST树，检查判断子节点是否在父节点的范围内，不在则修复
  handleParentChildrenAll("root", comps, event, MOUSE_ACTION_COMP_RESIZE)

  // 拖拽后，遍历所有的节点，处理普通布局的
  handlelLayoutAll("root", props, comps, action);

  console.log("props========================================", props);

  // 判断第一个节点是否有margin-top，有则父元素必须要设置 上边框 border-t-[0.000000001px] border-t-transparent （已存在则不添加）。处理 CSS 经典现象：**外边距塌陷（margin collapse，外边距合并）**，具体属于：**父子块级元素垂直外边距塌陷**。
  fixParentMarginCollapse("root", comps);
  console.log("components---------------------------------------2257", comps)
  return comps;

}


/**
 * @description 拖拽一个或多个节点后，处理flex布局的
 * @param prop  指定节点的prop
 * @param comps  AST数组
 * @param action 拖拽的方式
 * @returns
 */
export function handleFlexLayout(selectProps, pComp, comps, action) {
  console.log(action)

  let rectArr = getRectArr(comps)

  // 1.1、先获取 拖拽矩形（选择的一个或多个节点，取最左、最右、最上、最下的坐标）
  let minSelectX = null;
  let maxSelectX = null;
  let minSelectY = null;
  let maxSelectY = null;

  for (let selectProp of selectProps) {
    // 获取拖拽的矩形（批量选择多个节点的 最左边、最右边、最上边、最下边）
    let selectRect = rectArr.find(item => item.prop == selectProp);
    minSelectX = minSelectX < selectRect.left && minSelectX != null ? minSelectX : selectRect.left;
    maxSelectX = maxSelectX > selectRect.right && maxSelectX != null ? maxSelectX : selectRect.right;
    minSelectY = minSelectY < selectRect.top && minSelectY != null ? minSelectY : selectRect.top;
    maxSelectY = maxSelectY > selectRect.bottom && maxSelectY != null ? maxSelectY : selectRect.bottom;
  }

  // 获取该父节点下面的非文档流子节点
  let childrenTemp1 = pComp.children;
  let childStaticCompArr = [];  // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let j1 = 0; j1 < childrenTemp1.length; j1++) {
    let cComp = comps.find(item => item.prop === childrenTemp1[j1].prop && (selectProps.indexOf(item.prop) == -1 && (getPositionType(item) == "static" || getPositionType(item) == "sticky" || getPositionType(item) == "relative")));
    if (cComp) {
      childStaticCompArr.push(cComp)
    }
  }


  // 获取节点的样式
  let flexNormal = pComp.dynamicCss[0].sources[0].css.normal;
  // const flexWrap = flexNormal["flex-wrap"];   //  是否可以换行 "flex-wrap": "flex-wrap"
  const flexDirection = flexNormal["flex-direction"];   // 从上到下的 "flex-direction": "flex-col"。默认是从左到右

  // 1.2.2.1、可能性父节点的中心点坐标 + 拖拽坐标 + 勾股定理 获取最近的 可能性父节点 的节点A。
  // 初始化最小值对象为数组中第一个对象
  let minPoint = null;
  let minDistance = null;

  if (flexDirection == "flex-col") {  // 从上到下的
    // 取最近的那个节点
    for (let i1 = 0; i1 < childStaticCompArr.length; i1++) {
      let rectTemp = rectArr.find(item => item.prop === childStaticCompArr[i1].prop)
      if (minSelectY > rectTemp.top) {
        if (minDistance == null || minDistance > ((minSelectX - rectTemp.left) * (minSelectX - rectTemp.left) + (minSelectY - rectTemp.top) * (minSelectY - rectTemp.top))) {
          minDistance = ((minSelectX - rectTemp.left) * (minSelectX - rectTemp.left) + (minSelectY - rectTemp.top) * (minSelectY - rectTemp.top))
          minPoint = rectTemp
        }
      }
    }
  } else { // 从左到右
    // 取最近的那个节点
    for (let i1 = 0; i1 < childStaticCompArr.length; i1++) {
      let rectTemp = rectArr.find(item => item.prop === childStaticCompArr[i1].prop)
      if (minSelectX > rectTemp.left) {
        // 根据勾股定理，取最近的
        if (minDistance == null || minDistance > ((minSelectX - rectTemp.left) * (minSelectX - rectTemp.left) + (minSelectY - rectTemp.top) * (minSelectY - rectTemp.top))) {
          minDistance = ((minSelectX - rectTemp.left) * (minSelectX - rectTemp.left) + (minSelectY - rectTemp.top) * (minSelectY - rectTemp.top))
          minPoint = rectTemp
        }
      }
    }
  }


  // 获取选择的节点
  let selectCompArr = [];  // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let j1 = 0; j1 < selectProps.length; j1++) {
    let cComp = comps.find(item => item.prop === selectProps[j1]);
    if (cComp) {
      selectCompArr.push(cComp)
    }
  }

  if (action !== MOUSE_ACTION_COMP_RESIZE) {  // flex 和 grid 拖拽调整节点大小的，不改变父子关系和兄弟节点的顺序
    for (let i1 = 0; i1 < selectCompArr.length; i1++) { // pComp、selectComp

      if (getPositionType(selectCompArr[i1]) == "static" || getPositionType(selectCompArr[i1]) == "sticky" || getPositionType(selectCompArr[i1]) == "relative") {

        // 删除外边距
        let normal = selectCompArr[i1].dynamicCss[0].sources[0].css.normal
        delete normal["margin"];
        delete normal["margin-bottom"];
        delete normal["margin-left"];
        delete normal["margin-top"];
        delete normal["margin-right"];
        delete normal["bottom"];
        delete normal["left"];
        delete normal["top"];
        delete normal["right"];

        // 重新设置兄弟节点的顺序关系
        pComp.children = moveAfterByPropLoop(pComp.children, {prop: selectCompArr[i1].prop}, minPoint == null ? null : minPoint.prop)
        pComp.children = fixChildrenOrder(pComp.children, comps)
      } else {  // 绝对布局是不能拖进grid和flex布局的节点，只能拖进grid和flex布局的兄弟节点。这里加个处理绝对布局的拖进来还是绝对布局
        handleFelxGridObsolute(pComp, selectCompArr[i1], comps);
      }
    }
  }
}


/**
 * @description 拖拽一个或多个节点后，处理grid布局的
 * @param selectProps  指定节点的prop
 * @param pComp  Grid 父节点
 * @param comps  AST数组
 * @param action 拖拽的方式
 * @returns
 */
export function handleGridLayout(selectProps, pComp, comps, action) {
  console.log(action)

  let rectArr = getRectArr(comps);

  // 1.1、先获取 拖拽矩形（选择的一个或多个节点，取最左、最右、最上、最下的坐标）
  let minSelectX = null;
  let maxSelectX = null;
  let minSelectY = null;
  let maxSelectY = null;

  for (let selectProp of selectProps) {
    // 获取拖拽的矩形（批量选择多个节点的 最左边、最右边、最上边、最下边）
    let selectRect = rectArr.find(item => item.prop == selectProp);
    minSelectX = minSelectX < selectRect.left && minSelectX != null ? minSelectX : selectRect.left;
    maxSelectX = maxSelectX > selectRect.right && maxSelectX != null ? maxSelectX : selectRect.right;
    minSelectY = minSelectY < selectRect.top && minSelectY != null ? minSelectY : selectRect.top;
    maxSelectY = maxSelectY > selectRect.bottom && maxSelectY != null ? maxSelectY : selectRect.bottom;
  }

  // 获取该父节点下面的非文档流子节点
  let childrenTemp1 = pComp.children;
  let childStaticCompArr = [];  // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let j1 = 0; j1 < childrenTemp1.length; j1++) {
    let cComp = comps.find(item => item.prop === childrenTemp1[j1].prop && (selectProps.indexOf(item.prop) == -1 && (getPositionType(item) == "static" || getPositionType(item) == "sticky" || getPositionType(item) == "relative")));
    if (cComp) {
      childStaticCompArr.push(cComp)
    }
  }

  // 1.2.2.1、可能性父节点的中心点坐标 + 拖拽坐标 + 勾股定理 获取最近的 可能性父节点 的节点A。
  // 初始化最小值对象为数组中第一个对象
  let minPoint = null;
  let minDistance = null;

  // 取最近的那个节点
  for (let i1 = 0; i1 < childStaticCompArr.length; i1++) {
    let rectTemp = rectArr.find(item => item.prop === childStaticCompArr[i1].prop)
    if (minSelectX >= rectTemp.left) {
      // 根据勾股定理，取最近的
      if (minDistance == null || minDistance > ((minSelectX - rectTemp.left) * (minSelectX - rectTemp.left) + (minSelectY - rectTemp.top) * (minSelectY - rectTemp.top))) {
        minDistance = ((minSelectX - rectTemp.left) * (minSelectX - rectTemp.left) + (minSelectY - rectTemp.top) * (minSelectY - rectTemp.top))
        minPoint = rectTemp
      }
    }
  }

  // 获取选择的节点
  let selectCompArr = [];  // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let j1 = 0; j1 < selectProps.length; j1++) {
    let cComp = comps.find(item => item.prop === selectProps[j1]);
    if (cComp) {
      selectCompArr.push(cComp)
    }
  }

  // 处理拖拽节点的的跨行跨列的
  let p_dycss = parseTailwindToCss(pComp.dynamicCss[0].sources[0].css.normal)

  // 获取列数
  const gridTemplateColumns = p_dycss[GRID_TEMPLATE_COLUMNS]
  const gridColumnSize = parseInt((gridTemplateColumns ? gridTemplateColumns : "1")) // 没有配置列，默认1列

  // 获取行数
  const gridTemplateRows = p_dycss[GRID_TEMPLATE_ROWS]
  const gridRowSize = parseInt(gridTemplateRows ? gridTemplateRows : "1") // 没有配置行，默认1行

  // 获取表格节点dom的left、top、宽度、高度
  let pRect = rectArr.find(item => item.prop == pComp.prop);

  //获取列间距
  const columnGap = p_dycss["column-gap"] // 列间距
  const reColumnGapPx = parseInt((columnGap ? columnGap : "0")) * 4 // 间距换成px

  //获取行间距
  const rowGap = p_dycss["row-gap"] // 行间距
  const reRowGapPx = parseInt((rowGap ? rowGap : "0")) * 4 // 间距换成px

  // 获取 内边距
  const paddingLeft = p_dycss.hasOwnProperty(PADDING_LEFT) ? convertInlineCss2Number(parsePadding(p_dycss[PADDING_LEFT])) : convertInlineCss2Number(parsePadding(p_dycss[PADDING]))
  const paddingRight = p_dycss.hasOwnProperty(PADDING_RIGHT) ? convertInlineCss2Number(parsePadding(p_dycss[PADDING_RIGHT])) : convertInlineCss2Number(parsePadding(p_dycss[PADDING]))
  const paddingTop = p_dycss.hasOwnProperty(PADDING_TOP) ? convertInlineCss2Number(parsePadding(p_dycss[PADDING_TOP])) : convertInlineCss2Number(parsePadding(p_dycss[PADDING]))
  const paddingBottom = p_dycss.hasOwnProperty(PADDING_BOTTOM) ? convertInlineCss2Number(parsePadding(p_dycss[PADDING_BOTTOM])) : convertInlineCss2Number(parsePadding(p_dycss[PADDING]))

  // 均分宽度
  let widthSvg = ((convertInlineCss2Number(pRect.width) - (gridColumnSize - 1) * reColumnGapPx) - paddingLeft - paddingRight) / gridColumnSize

  // 均分高度
  let heightSvg = ((convertInlineCss2Number(pRect.height) - (gridRowSize - 1) * reRowGapPx) - paddingTop - paddingBottom) / gridRowSize

  // 切割成一个个格子的坐标
  let gridColumnRowArray = [];
  for (let i2 = 0; i2 < gridRowSize; i2++) {
    let rowArr = [];
    for (let j1 = 0; j1 < gridColumnSize; j1++) {
      let obj = {
        row: i2 + 1,
        column: j1 + 1,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: 0,
        height: 0
      }
      obj.left = pRect.left + paddingLeft + j1 * (widthSvg + reColumnGapPx);
      obj.right = obj.left + widthSvg;
      obj.top = pRect.top + paddingTop + i2 * (heightSvg + reRowGapPx);
      obj.bottom = obj.top + heightSvg;
      obj.width = widthSvg;
      obj.height = heightSvg;
      rowArr.push(obj);
    }
    gridColumnRowArray.push(rowArr);
  }


  // ⭐ 检查 Grid 是否已满（排除正在拖拽的元素）
  const gridResult = checkGridHaveCell(pComp.prop, comps, null, null, null, selectProps);

  for (let i1 = 0; i1 < selectCompArr.length; i1++) {
    const selectComp = selectCompArr[i1];

    if (getPositionType(selectComp) == "static" || getPositionType(selectComp) == "sticky" || getPositionType(selectComp) == "relative") {

      // ⭐ Grid 已满或溢出，将元素移出 Grid 成为兄弟节点（absolute 定位）
      if (gridResult === 'full' || gridResult === 'overflow') {
        // 1. 获取 Grid 的父节点
        const gridParentProp = pComp.parent;
        const gridParentComp = comps.find(c => c.prop === gridParentProp);

        // 2. 从 Grid 中移除
        pComp.children = pComp.children.filter(c => c.prop !== selectComp.prop);

        // 3. 添加到 Grid 父节点下（成为 Grid 的兄弟）
        const gridIndex = gridParentComp.children.findIndex(c => c.prop === pComp.prop);
        if (gridIndex !== -1) {
          gridParentComp.children.splice(gridIndex + 1, 0, {prop: selectComp.prop});
        } else {
          gridParentComp.children.push({prop: selectComp.prop});
        }

        // 4. 更新父节点
        selectComp.parent = gridParentProp;

        // 5. 改为 absolute 定位
        _set(selectComp, 'dynamicCss[0].sources[0].css.normal.position', 'absolute');

        // 6. 清除相关样式
        const normal = selectComp.dynamicCss[0].sources[0].css.normal;
        delete normal.margin;
        delete normal['margin-left'];
        delete normal['margin-right'];
        delete normal['margin-top'];
        delete normal['margin-bottom'];
        delete normal['grid-column'];
        delete normal['grid-row'];
        delete normal['grid-column-start'];
        delete normal['grid-row-start'];
        delete normal.left;
        delete normal.top;

        // 7. 确保 Grid 父节点是 relative
        if (getPositionType(gridParentComp) === 'static' || getPositionType(gridParentComp) === 'sticky') {
          _set(gridParentComp, 'dynamicCss[0].sources[0].css.normal.position', 'relative');
        }

        // 8. 计算相对于 Grid 父节点的位置
        const rectArr = getRectArr(comps);
        const sRect = rectArr.find(item => item.prop === selectComp.prop);
        const pRect = rectArr.find(item => item.prop === gridParentProp);

        if (sRect && pRect) {
          _set(selectComp, 'dynamicCss[0].sources[0].css.normal.left',
            parseCssPx2TailwindRem(LEFT, sRect.left - pRect.left));
          _set(selectComp, 'dynamicCss[0].sources[0].css.normal.top',
            parseCssPx2TailwindRem(TOP, sRect.top - pRect.top));
          _set(selectComp, 'dynamicCss[0].sources[0].css.normal.width',
            parseCssPx2TailwindRem(WIDTH, sRect.width));
          _set(selectComp, 'dynamicCss[0].sources[0].css.normal.height',
            parseCssPx2TailwindRem(HEIGHT, sRect.height));
        }

        // 修复 z-index
        fixZIndex(selectComp.prop, comps);
        continue;
      }

      // ⭐ Grid 未满，正常处理 Grid 布局
      // 删除外边距
      let normal = selectComp.dynamicCss[0].sources[0].css.normal
      delete normal["margin"];
      delete normal["margin-bottom"];
      delete normal["margin-left"];
      delete normal["margin-top"];
      delete normal["margin-right"];
      delete normal["bottom"];
      delete normal["left"];
      delete normal["top"];
      delete normal["right"];
      delete normal["grid-column-start"];  // 起始列
      delete normal["grid-row-start"];     // 起始行
      delete normal["grid-column"];        // 跨列
      delete normal["grid-row"];           // 跨行

      // 重新设置兄弟节点的顺序关系
      pComp.children = moveAfterByPropLoop(pComp.children, {prop: selectComp.prop}, minPoint == null ? null : minPoint.prop)
      pComp.children = fixChildrenOrder(pComp.children, comps)

      let sRect = rectArr.find(item => item.prop == selectComp.prop);
      let minStart = null;
      let minEnd = null;
      let minStartDistance = null;
      let minEndDistance = null;
      // 重新设置 left、top、起始行、起始列
      for (let i2 = 0; i2 < gridColumnRowArray.length; i2++) {
        for (let i3 = 0; i3 < gridColumnRowArray[i2].length; i3++) {
          // 获取左上角的 起始列和起始行
          if (gridColumnRowArray[i2][i3].left <= sRect.left && gridColumnRowArray[i2][i3].top <= sRect.top) {
            let minStartDistance2 = (sRect.left - gridColumnRowArray[i2][i3].left) * (sRect.left - gridColumnRowArray[i2][i3].left) + (sRect.top - gridColumnRowArray[i2][i3].top) * (sRect.top - gridColumnRowArray[i2][i3].top)
            if (minStartDistance == null || minStartDistance > minStartDistance2) {
              minStart = gridColumnRowArray[i2][i3];
              minStartDistance = minStartDistance2;
            }
          }
          // 获取右下角的 结束列和结束行
          if (gridColumnRowArray[i2][i3].right >= sRect.right && gridColumnRowArray[i2][i3].bottom >= sRect.bottom) {
            let minEndDistance2 = (gridColumnRowArray[i2][i3].right - sRect.right) * (gridColumnRowArray[i2][i3].right - sRect.right) + (gridColumnRowArray[i2][i3].bottom - sRect.bottom) * (gridColumnRowArray[i2][i3].bottom - sRect.bottom)
            if (minEndDistance == null || minEndDistance > minEndDistance2) {
              minEnd = gridColumnRowArray[i2][i3];
              minEndDistance = minEndDistance2;
            }
          }
        }
      }

      if ((minEnd.column - minStart.column + 1) > 1) {
        // 重新设置 跨行、跨列
        _set(selectComp, 'dynamicCss[0].sources[0].css.normal.grid-column', "col-span-" + (minEnd.column - minStart.column + 1))
      }
      if ((minEnd.row - minStart.row + 1) > 1) {
        // 重新设置 跨行、跨列
        _set(selectComp, 'dynamicCss[0].sources[0].css.normal.grid-row', "row-span-" + (minEnd.row - minStart.row + 1))
      }

    } else {
      // 绝对布局的处理（不影响执行，暂时保留）
      handleFelxGridObsolute(pComp, selectComp, comps);
    }
  }
}

/**
 * @description 检查所有节点的父子关系是否正确，然后修复父子关系的
 * @param { Set<string> } propSet
 * @returns
 */
export function handleParentChildrenAll(prop, comps, event, action) {

  let rectArr = getRectArr(comps);

  let sComp = comps.find(c => c.prop === prop);

  // ⭐ 如果当前节点隐藏，直接返回（不处理隐藏节点的子节点关系）
  const sDycss = parseToCssDefault(sComp)
  if (sDycss[DISPLAY] === 'none') {
    return
  }

  // 获取全部子节点
  // let childrenCompArr = comps.filter(item => item.parent == sComp.prop);
  // ⭐ 如果是 fixed 定位，只检查它的子节点，不检查它自己的父节点范围
  const isFixed = getPositionType(sComp) === 'fixed';
  // 获取该父节点下面的文档流子节点(dragProps是拖拽移动的节点，拖拽移动的节点要默认是绝对布局absolute，忽略以前的定位)
  let childrenTemp4 = sComp.children;
  let childrenCompArr = [];  // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let j1 = 0; j1 < childrenTemp4.length; j1++) {
    let cComp = comps.find(item => item.prop === childrenTemp4[j1].prop);
    if (cComp) {
      const cDycss = parseToCssDefault(cComp)
      // ⭐ 跳过隐藏的子节点
      if (cDycss[DISPLAY] === 'none') {
        continue
      }
      childrenCompArr.push(cComp)
    }
  }
  // ⭐ 如果不是 fixed，检查子节点是否在父节点的范围内
  if (!isFixed) {
    // 检查子节点是否在父节点的范围内
    for (let i1 = 0; i1 < childrenCompArr.length; i1++) {

      // 获取父节点的dom的 left坐标、right坐标、高度、宽度
      let rect = rectArr.find(item => item.prop === childrenCompArr[i1].prop);

      let minSelectX = rect.left;
      let maxSelectX = rect.right;
      let minSelectY = rect.top;
      let maxSelectY = rect.bottom;

      if (checkOutRange(minSelectX, maxSelectX, minSelectY, maxSelectY, prop) != true) {  // 检查子节点的是否在父节点的范围内
        //  flex 和 grid 布局的文档流是不会重新找父节点的
        if ((getLayoutType(sComp) != "flex" && getLayoutType(sComp) != "grid") || (getPositionType(childrenCompArr[i1]) != "static" && getPositionType(childrenCompArr[i1]) != "sticky" && getPositionType(childrenCompArr[i1]) != "relative")) {
          // 不在则修复，重新找最近的父节点
          handleParentChildren([childrenCompArr[i1].prop], comps, event, action);
        }
      } else {
        for (let i2 = 0; i2 < childrenCompArr.length; i2++) {   // 检查节点是否被其他兄弟节点包裹
          if (childrenCompArr[i1].prop != childrenCompArr[i2].prop) {
            if (checkOutRange(minSelectX, maxSelectX, minSelectY, maxSelectY, childrenCompArr[i2].prop) == true && getPositionType(childrenCompArr[i1]) != "static" && getPositionType(childrenCompArr[i1]) != "sticky" && getPositionType(childrenCompArr[i1]) != "relative" && (childrenCompArr[i2].type === 'frame' || childrenCompArr[i2].type === 'cyclic-region' || childrenCompArr[i2].type === 'group')) {
              if (getLayoutType(childrenCompArr[i2]) != "flex" && getLayoutType(childrenCompArr[i2]) != "grid") {
                // 不在则修复，重新找最近的父节点
                handleParentChildren([childrenCompArr[i1].prop], comps, event, action);
              }
            }
          }
        }
      }

      // 递归调用子节点
      handleParentChildrenAll(childrenCompArr[i1].prop, comps, event, action);
    }
  } else {
    // ⭐ fixed 节点：只递归处理子节点，不检查父节点范围
    for (const childComp of childrenCompArr) {
      handleParentChildrenAll(childComp.prop, comps, event, action);
    }
  }
}


/**
 * @description 处理 fixed 定位组件的拖拽逻辑
 * - fixed 的父节点只能是 root
 * - fixed 的 top/left 转换成 margin-top/margin-left 存储
 * @param {string[]} props 拖拽的节点 prop 数组
 * @param {ComponentItem[]} comps AST 组件数组
 * @param event
 * @returns {ComponentItem[]} 处理后的组件数组
 */
export const handleFixedDrag = (props: string[], comps: ComponentItem[], event: any): ComponentItem[] => {
  for (const prop of props) {
    const comp = comps.find(c => c.prop === prop);
    const compXY = event.find(c => c.prop === prop);
    if (!comp) continue;

    const s_dycss = parseToCssDefault(comp);
    const s_position = s_dycss[POSITION];

    // 只处理 fixed 定位的组件
    if (s_position !== 'fixed') continue;

    // 获取当前 DOM 元素
    const s_el = document.getElementById(prop);
    if (!s_el) continue;

    // 确保 fixed 元素的父节点是 root
    if (comp.parent !== 'root') {
      // 从原父节点移除
      const oldParent = comps.find(c => c.prop === comp.parent);
      if (oldParent) {
        oldParent.children = oldParent.children.filter(c => c.prop !== comp.prop);
      }
      // 添加到 root
      const rootComp = comps.find(c => c.prop === 'root');
      if (rootComp) {
        rootComp.children.push({prop: comp.prop});
      }
      comp.parent = 'root';
    }

    // 确保 position 为 fixed
    _set(comp, 'dynamicCss[0].sources[0].css.normal.position', 'fixed');
    _set(comp, 'dynamicCss[0].sources[0].css.normal.top', parseCssPx2TailwindRem(TOP, compXY.top))
    _set(comp, 'dynamicCss[0].sources[0].css.normal.left', parseCssPx2TailwindRem(LEFT, compXY.left))

    // 修复 z-index
    fixZIndex(comp.prop, comps);
  }
  return comps;
};


/**
 * @description 重新根据下面的规则找父亲节点，然后处理父子关系的
 * @param props  选择的多个节点
 * @param comps  AST节点
 * @param event 事件
 * @param action 拖拽方式
 * @returns
 */
export function handleParentChildren(props, comps, event, action) {

  let rectArr = getRectArr(comps);

  if (action === MOUSE_ACTION_COMP_RESIZE) {  // flex 和 grid 拖拽调整节点大小的，不改变父子关系和兄弟节点的顺序
    let selComp = comps.find(c => c.prop == props[0]);
    let pComp = comps.find(c => c.prop == selComp.parent);
    if (selComp && (getLayoutType(pComp) == "flex" || getLayoutType(pComp) == "grid")) {
      return [{prop: props[0], oldParent: selComp.parent, newParent: selComp.parent}]
    }
  }

  console.log(event)
  console.log(action)

  // 1.1、先获取 拖拽矩形（选择的一个或多个节点，取最左、最右、最上、最下的坐标）
  let minSelectX = null;
  let maxSelectX = null;
  let minSelectY = null;
  let maxSelectY = null;
  let ignorePropArr = [];  // 已选的节点的prop，及其后代节点的prop（后面的判断需要用来忽略选中节点的）
  let oldNewParent = [];      // 返回的数组
  let hasAbolute = false;  // 判断选择的节点是否有 绝对布局absolute

  for (let selectProp of props) {
    let oldNewObj = {prop: selectProp};
    // 获取拖拽的矩形（批量选择多个节点的 最左边、最右边、最上边、最下边）
    let selectRect = rectArr.find(item => item.prop == selectProp);
    minSelectX = minSelectX < selectRect.left && minSelectX != null ? minSelectX : selectRect.left;
    maxSelectX = maxSelectX > selectRect.right && maxSelectX != null ? maxSelectX : selectRect.right;
    minSelectY = minSelectY < selectRect.top && minSelectY != null ? minSelectY : selectRect.top;
    maxSelectY = maxSelectY > selectRect.bottom && maxSelectY != null ? maxSelectY : selectRect.bottom;

    // 给 ignorePropArr 设置值，
    getDescendantsPropArr(ignorePropArr, selectProp, comps);

    // 获取当前选择节点的父节点。
    let selComp = comps.find(c => c.prop == selectProp)
    oldNewObj["oldParent"] = selComp.parent;

    if (getPositionType(selComp) != "static" && getPositionType(selComp) != "sticky" && getPositionType(selComp) != "relative") {
      hasAbolute = true;
    }

    oldNewParent.push(oldNewObj)
  }

  // ⭐ 检查拖拽的节点中是否有 fixed 定位的组件
  let hasFixed = false;
  for (const selectProp of props) {
    const selComp = comps.find(c => c.prop == selectProp);
    if (selComp && getPositionType(selComp) === 'fixed') {
      hasFixed = true;
      break;
    }
  }

  // ⭐ 如果有 fixed 组件，直接返回，不改变父子关系（fixed 的父节点只能是 root）
  if (hasFixed) {
    return props.map(prop => {
      const comp = comps.find(c => c.prop === prop);
      return {prop, oldParent: comp?.parent || 'root', newParent: 'root'};
    });
  }

  // 查找所有的最近的父组件
  let p_range = []
  for (const comp of comps) {

    let cRect = rectArr.find(item => item.prop == comp.prop);

    // 获取所有在范围内的父节点
    // 1、选择的节点，及其子节点都忽略
    // 2、隐藏的节点都忽略
    // 3、如果选择的节点有 absolute，则忽略 flex 和 grid 布局的
    // 4、'frame'、'cyclic-region'、'group' 才能有子节点
    if (ignorePropArr.indexOf(comp.prop) == -1 && cRect.display != "none" && (hasAbolute == false || (getLayoutType(comp) != "flex" && getLayoutType(comp) != "grid")) && (comp.type === 'frame' || comp.type === 'cyclic-region' || comp.type === 'group')) {
      let pRect = rectArr.find(item => item.prop == comp.prop);

      const p_el_left = pRect.left   // 左上角x坐标
      const p_el_top = pRect.top     // 左上角y坐标
      const p_el_w = pRect.width     // 宽
      const p_el_h = pRect.height    // 高

      let p_x = p_el_left + p_el_w    // 右下角x坐标
      let p_y = p_el_top + p_el_h     // 右下角y坐标

      // 获取所有 可能性的父节点，必须要整个都拖进才算是
      if (p_el_left <= minSelectX && maxSelectX <= p_x && p_el_top <= minSelectY && maxSelectY <= p_y) {
        p_range.push({
          prop: comp.prop,
          x: p_x,
          y: p_y,
          w: p_el_w,
          h: p_el_h,
          centerX: (p_x + p_el_w / 2),
          centerY: (p_y + p_el_h / 2)
        })
      }
    }
  }

  // 获取index层级最高的 新父节点
  const pMinProp = findMinXY(p_range, comps);
  // 将当前组件落到父节点上
  const newPComp = comps.find(c => c.prop === pMinProp)

  // ⭐ 检查 Grid 是否已满（排除正在拖拽的元素）
  let gridResult = null;
  if (newPComp && getLayoutType(newPComp) === 'grid') {
    gridResult = checkGridHaveCell(pMinProp, comps, null, null, null, props);
  }

  // 根据 oldNewParent 修复父子关系，并设置返回结果
  for (let obj of oldNewParent) {
    // 去除原来的父子关系
    const oldPComp = comps.find(c => c.prop === obj.oldParent);
    oldPComp.children = oldPComp.children.filter(c => c.prop != obj.prop);

    // 修复原父节点及其祖先节点高宽为 auto，普通布局的高宽是不会变的（拖拽调整节点大小的除外），只有 flex 和 grid 的高宽才会变化
    if (obj.oldParent != pMinProp && (getLayoutType(oldPComp) == "flex" || getLayoutType(oldPComp) == "grid")) {
      let wh = getWidthHeight(oldPComp)
      if (["w-auto", "w-fit"].indexOf(wh.width) != -1 || ["h-auto", "h-fit"].indexOf(wh.height) != -1) {
        let renderProp = getRenderProp(oldPComp.prop, comps);
        localRecurseCalcCompsXY(renderProp, comps);
      }
    }

    const comp = comps.find(c => c.prop === obj.prop);

    // ⭐ Grid 已满或溢出，将拖入的元素转为 Grid 的兄弟节点（absolute 定位）
    if (gridResult === 'full' || gridResult === 'overflow') {
      // 1. 获取 Grid 容器的父节点（爷爷节点）
      const gridParentProp = newPComp.parent;
      const gridParentComp = comps.find(c => c.prop === gridParentProp);

      // 2. 将当前组件从 Grid 的子节点中移除（确保不在 Grid 的 children 中）
      newPComp.children = newPComp.children.filter(c => c.prop !== comp.prop);

      // 3. 将当前组件添加到 Grid 容器的父节点下（成为 Grid 的兄弟节点）
      // 找到 Grid 在父节点 children 中的位置，插入到 Grid 后面
      const gridIndex = gridParentComp.children.findIndex(c => c.prop === newPComp.prop);
      if (gridIndex !== -1) {
        // 在 Grid 后面插入
        gridParentComp.children.splice(gridIndex + 1, 0, {prop: comp.prop});
      } else {
        // 如果找不到，直接添加到末尾
        gridParentComp.children.push({prop: comp.prop});
      }

      // 4. 更新当前组件的父节点为 Grid 的父节点
      comp.parent = gridParentProp;

      // 5. 将当前组件改为 absolute 定位（相对于 Grid 的父节点定位）
      _set(comp, 'dynamicCss[0].sources[0].css.normal.position', 'absolute');

      // 6. 清除 margin 和 grid 相关样式
      const normal = comp.dynamicCss[0].sources[0].css.normal;
      delete normal.margin;
      delete normal['margin-left'];
      delete normal['margin-right'];
      delete normal['margin-top'];
      delete normal['margin-bottom'];
      delete normal['grid-column'];
      delete normal['grid-row'];
      delete normal['grid-column-start'];
      delete normal['grid-row-start'];
      delete normal.left;
      delete normal.top;

      // 7. 确保 Grid 的父节点是 relative（作为 absolute 的参考）
      if (getPositionType(gridParentComp) === 'static' || getPositionType(gridParentComp) === 'sticky') {
        _set(gridParentComp, 'dynamicCss[0].sources[0].css.normal.position', 'relative');
      }

      // 8. 计算相对于 Grid 父节点的位置
      const rectArr = getRectArr(comps);
      const sRect = rectArr.find(item => item.prop === comp.prop);
      const pRect = rectArr.find(item => item.prop === gridParentProp);

      if (sRect && pRect) {
        _set(comp, 'dynamicCss[0].sources[0].css.normal.left',
          parseCssPx2TailwindRem(LEFT, sRect.left - pRect.left));
        _set(comp, 'dynamicCss[0].sources[0].css.normal.top',
          parseCssPx2TailwindRem(TOP, sRect.top - pRect.top));
        _set(comp, 'dynamicCss[0].sources[0].css.normal.width',
          parseCssPx2TailwindRem(WIDTH, sRect.width));
        _set(comp, 'dynamicCss[0].sources[0].css.normal.height',
          parseCssPx2TailwindRem(HEIGHT, sRect.height));
      }

      // 9. 修复 z-index
      fixZIndex(comp.prop, comps);

      // 10. 更新返回结果
      obj["newParent"] = gridParentProp;

    } else {
      // Grid 未满，正常处理（成为 Grid 的子节点）
      const newPComp = comps.find(c => c.prop === pMinProp);
      newPComp.children.push({prop: obj.prop});
      comp.parent = pMinProp;
      obj["newParent"] = pMinProp;

      // 修复 z-index
      fixZIndex(comp.prop, comps);

      // 删除子元素的部分属性，如高宽为full的
      let normal = comp.dynamicCss[0].sources[0].css.normal;
      if (normal["width"] === "w-full") {
        let cRect = rectArr.find(item => item.prop === comp.prop);
        _set(comp, 'dynamicCss[0].sources[0].css.normal.width', parseCssPx2TailwindRem(WIDTH, cRect.width));
      }
      if (normal["height"] === "h-full") {
        let cRect = rectArr.find(item => item.prop === comp.prop);
        _set(comp, 'dynamicCss[0].sources[0].css.normal.height', parseCssPx2TailwindRem(HEIGHT, cRect.height));
      }
    }
  }

  // 1.2.2.3、返回{拖拽节点prop、新父节点的prop、原父节点prop}
  return oldNewParent;
}

export function fixZIndex(prop, comps) {

  if (!comps) {
    comps = _cloneDeep(astStore.state.components)
  }

  if (prop != "root") {
    const comp = comps.find(c => c.prop === prop);
    const pComp = comps.find(c => c.prop === comp.parent);

    // 重新设置层级 z-index
    let p_z = parseToCssDefault(pComp)[Z_INDEX]
    if (pComp.prop == "root") {
      _set(comp, 'dynamicCss[0].sources[0].css.normal.z-index', 'z-1')
    } else if (p_z === 'auto') {
      _set(comp, 'dynamicCss[0].sources[0].css.normal.z-index', 'z-auto')
    } else {
      _set(comp, 'dynamicCss[0].sources[0].css.normal.z-index', `z-${convertInlineCss2Number(p_z) + 1}`)
    }

    let children = comp.children;

    for (let i1 = 0; i1 < children.length; i1++) {
      fixZIndex(children[i1].prop, comps)
    }

  }

}

/**
 * @description 处理指定节点的普通布局
 * @param prop  指定节点的prop
 * @param dragProps
 * @param comps  AST数组
 * @param action 拖拽的方式
 * @returns
 */
export function handlelLayoutAll(prop, dragProps, comps, action) {

  let sComp = comps.find(c => c.prop === prop);

  // 获取全部子节点
  let children = sComp.children;

  // 没有子节点则不需要执行
  if (children.length == 0) {
    return;
  }
  for (let i1 = 0; i1 < children.length; i1++) {
    handlelLayoutAll(children[i1].prop, dragProps, comps, action)
  }
  handleNormalLayout(prop, dragProps, comps, action);
}


/** dynamicCss 中 normal 样式的固定访问路径 */
const NORMAL_PATH = 'dynamicCss[0].sources[0].css.normal';

/** 设置某个组件 normal 样式下的某个属性 */
function setNormal(comp, key, val) {
  _set(comp, `${NORMAL_PATH}.${key}`, val);
}

/** 删除某个组件 normal 样式下的若干属性（用于清理旧定位残留） */
function delNormal(comp, ...keys) {
  const normal = comp.dynamicCss[0].sources[0].css.normal;
  keys.forEach(k => delete normal[k]);
}

/** 获取某个组件 normal 样式对象 */
function getNormal(comp) {
  return comp.dynamicCss[0].sources[0].css.normal;
}

/**
 * 收集父节点下的子节点（按 pComp.children 顺序，不能直接用 comps.filter）
 * @param {Object}  pComp       父节点
 * @param {Array}   comps       AST 数组
 * @param {Array}   dragProps   本次拖拽的节点 prop 列表
 * @param {boolean} excludeDrag true=只收文档流节点(排除拖拽)；false=只收非文档流节点或拖拽节点
 */
function collectStaticChildren(pComp, comps, dragProps, excludeDrag) {
  const result = [];
  for (const child of pComp.children) {
    const cComp = comps.find(item => item.prop === child.prop);
    if (!cComp) continue;

    const cDycss = parseToCssDefault(cComp);
    // 跳过隐藏节点
    if (cDycss[DISPLAY] === 'none') continue;
    // 跳过 fixed 定位（fixed 不参与普通布局计算）
    if (getPositionType(cComp) === 'fixed') continue;

    const pos = getPositionType(cComp);
    // static / sticky / relative 视为参与文档流
    const isStatic = pos === 'static' || pos === 'sticky' || pos === 'relative';
    const isDrag = dragProps.indexOf(cComp.prop) !== -1;

    // excludeDrag=true  -> 收集文档流节点（排除拖拽节点）
    // excludeDrag=false -> 收集非文档流节点 或 拖拽节点
    if (excludeDrag ? (!isDrag && isStatic) : (isDrag || !isStatic)) {
      result.push(cComp);
    }
  }
  return result;
}


/**
 * @description 处理指定节点的普通布局
 * @param prop      指定节点的 prop
 * @param dragProps 本次拖拽的节点 prop 列表
 * @param comps     AST 数组
 * @param action    拖拽方式
 */
export function handleNormalLayout(prop, dragProps, comps, action) {
  console.log(action);

  // 拿到所有节点的 rect，后续布局计算都基于它
  const rectArr = getRectArr(comps);

  const pComp = comps.find(c => c.prop === prop);
  // flex / grid 布局不走普通布局逻辑
  if (getLayoutType(pComp) === 'flex' || getLayoutType(pComp) === 'grid') return;

  // 收集非文档流子节点（后续判断是否要转文档流）
  const childNotStaticCompArr = collectStaticChildren(pComp, comps, dragProps, false);

  // 父节点的 rect、内边距
  const pRect = rectArr.find(c => c.prop === pComp.prop);
  const pNormal = getNormal(pComp);
  const paddingObj1 = parseTailwindPadding(
    [pNormal['padding'], pNormal['padding-left'], pNormal['padding-top'],
      pNormal['padding-bottom'], pNormal['padding-right']].join(' ')
  );

  // 收集文档流子节点（排除拖拽节点，拖拽节点默认按 absolute 处理）
  const childStaticCompArr = collectStaticChildren(pComp, comps, dragProps, true);

  // 修复 relative 定位：用 margin 替换 left/top，并重算与上下兄弟的间距
  for (let i1 = 0; i1 < childStaticCompArr.length; i1++) {
    const childComp1 = childStaticCompArr[i1];
    const childRect = rectArr.find(c => c.prop === childComp1.prop);

    // relative 定位的子节点：若内部没有 absolute 子节点，直接降级为 static
    if (getPositionType(childComp1) === 'relative') {
      if (!hasAbsoluteChildren(childComp1.prop, comps)) {
        setNormal(childComp1, 'position', 'static');
      }
      // left/top/right/bottom 已由 margin 接管，清理掉
      delNormal(childComp1, 'left', 'top', 'right', 'bottom');
    }

    // 上下相邻的文档流节点（用于计算 margin）
    const recentlyTopComp = i1 !== 0 ? childStaticCompArr[i1 - 1] : null;
    const recentlyTopRect = recentlyTopComp ? rectArr.find(c => c.prop === recentlyTopComp.prop) : null;
    const recentlyBottomComp = i1 !== childStaticCompArr.length - 1 ? childStaticCompArr[i1 + 1] : null;
    const recentlyBottomRect = recentlyBottomComp ? rectArr.find(c => c.prop === recentlyBottomComp.prop) : null;

    // 左边距：相对父节点内容区左侧
    setNormal(childComp1, 'margin-left',
      parseCssPx2TailwindRem(MARGIN_LEFT, childRect.left - pRect.left - paddingObj1.left));

    // 上边距：有上邻节点就对齐上邻底部，否则对齐父节点内容区顶部
    if (recentlyTopComp) {
      const gap = childRect.top - recentlyTopRect.bottom;
      setNormal(childComp1, 'margin-top', parseCssPx2TailwindRem(MARGIN_TOP, gap));
      setNormal(recentlyTopComp, 'margin-bottom', parseCssPx2TailwindRem(MARGIN_BOTTOM, gap));
      delNormal(recentlyTopComp, 'bottom');
    } else {
      setNormal(childComp1, 'margin-top',
        parseCssPx2TailwindRem(MARGIN_TOP, childRect.top - pRect.top - paddingObj1.top));
    }

    // 下边距：有下邻节点就撑到下邻顶部，否则撑到父节点内容区底部
    if (recentlyBottomComp) {
      const gap = recentlyBottomRect.top - childRect.bottom;
      setNormal(childComp1, 'margin-bottom', parseCssPx2TailwindRem(MARGIN_BOTTOM, gap));
      setNormal(recentlyBottomComp, 'margin-top', parseCssPx2TailwindRem(MARGIN_TOP, gap));
      delNormal(recentlyBottomComp, 'top');
    } else {
      setNormal(childComp1, 'margin-bottom',
        parseCssPx2TailwindRem(MARGIN_BOTTOM, pRect.bottom - childRect.bottom - paddingObj1.bottom));
    }
  }
  let childrenChanged = false;
  // 遍历非文档流节点：不重叠 -> 转文档流；重叠 -> 转 absolute
  for (const childComp1 of childNotStaticCompArr) {
    const childRect1 = rectArr.find(c => c.prop === childComp1.prop);
    let isOverlap = false;
    let recentlyTop = null;          // 与上邻节点的最小间距（用于挑选"最近"的上邻）
    let recentlyTopComp = null;
    let recentlyTopRect = null;
    let recentlyBottomComp = null;
    let recentlyBottomRect = null;

    // 每次重新取，因为前面的处理可能已经改变了文档流集合
    const latestChildStaticCompArr = collectStaticChildren(pComp, comps, dragProps, true);

    // 先判断是否越出父节点内容区（与内边距重叠）
    if (childRect1.left < pRect.left + paddingObj1.left ||
      childRect1.top < pRect.top + paddingObj1.top ||
      childRect1.right > pRect.right - paddingObj1.right ||
      childRect1.bottom > pRect.bottom - paddingObj1.bottom) {
      isOverlap = true;
    } else {
      // 再判断是否与其他文档流节点重叠，并顺带找出上下最近邻
      for (const childComp2 of latestChildStaticCompArr) {
        const childRect2 = rectArr.find(c => c.prop === childComp2.prop);
        if (isRectOverlap(childRect1, childRect2)) {
          isOverlap = true;
          break;
        }

        const diff = childRect1.top - childRect2.top;
        if (diff > 0) {
          // 在下方，记录最近的上邻（diff 最小）
          if (recentlyTop == null || recentlyTop > diff) {
            recentlyTop = diff;
            recentlyTopComp = childComp2;
            recentlyTopRect = childRect2;
          }
        } else {
          // 在上方，作为下邻并结束（后面的都更靠上）
          recentlyBottomComp = childComp2;
          recentlyBottomRect = childRect2;
          break;
        }
      }
    }

    // 该节点已被消费，从 dragProps 中移除（原地删除，保持引用）
    const idx = dragProps.indexOf(childComp1.prop);
    if (idx > -1) dragProps.splice(idx, 1);

    // 根据重叠结果分流处理
    if (isOverlap) {
      handleOverlapToAbsolute(pComp, childComp1, pRect, childRect1);
      childrenChanged = true;
    } else {
      handleNonOverlapToFlow(
        pComp, childComp1, pRect, childRect1, paddingObj1,
        recentlyTopComp, recentlyTopRect,
        recentlyBottomComp, recentlyBottomRect,
        comps
      );
      childrenChanged = true;
    }
  }

  // 循环结束后统一同步 DOM
  if (childrenChanged) {
    reorderChildrenDom(pComp, getParentAllChildren(comps, pComp));
  }
}

/**
 * 重叠处理：把子节点改为 absolute 定位
 * - 父节点若是 static/sticky 且非 root，需要提升为 relative 作为定位参照
 * - 子节点清掉 margin，改用 left/top 定位
 */
function handleOverlapToAbsolute(pComp, childComp1, pRect, childRect1) {
  const pPos = getPositionType(pComp);
  // 父节点需要有定位上下文，子节点的 absolute 才会以父节点为参照
  if ((pPos === 'static' || pPos === 'sticky') && pComp.prop !== 'root') {
    setNormal(pComp, 'position', 'relative');
    delNormal(pComp, 'left', 'top');
  }

  setNormal(childComp1, 'position', 'absolute');
  setNormal(childComp1, 'left', parseCssPx2TailwindRem(LEFT, childRect1.left - pRect.left));
  setNormal(childComp1, 'top', parseCssPx2TailwindRem(TOP, childRect1.top - pRect.top));
  // absolute 定位后，margin 不再参与布局，清掉避免干扰
  delNormal(childComp1, 'margin', 'margin-left', 'margin-right', 'margin-top', 'margin-bottom');
}

/**
 * 不重叠处理：把子节点改为文档流
 * - 子节点若含 absolute 子级 -> relative，否则 -> static
 * - 用 margin 复现视觉位置，并同步调整上下邻节点的 margin
 * - 最后重排兄弟顺序，保证 DOM 顺序与视觉顺序一致
 */
function handleNonOverlapToFlow(
  pComp, childComp1, pRect, childRect1, paddingObj1,
  recentlyTopComp, recentlyTopRect,
  recentlyBottomComp, recentlyBottomRect,
  comps
) {
  // 含 absolute 子级时需要 relative 作为定位参照
  setNormal(childComp1, 'position',
    hasAbsoluteChildren(childComp1.prop, comps) ? 'relative' : 'static');

  // 左边距：相对父节点内容区左侧
  setNormal(childComp1, 'margin-left',
    parseCssPx2TailwindRem(MARGIN_LEFT, childRect1.left - pRect.left - paddingObj1.left));

  // 上边距：有上邻节点则与之保持间距，同时反向设置上邻的 margin-bottom
  if (recentlyTopComp) {
    const gap = childRect1.top - recentlyTopRect.bottom;
    setNormal(childComp1, 'margin-top', parseCssPx2TailwindRem(MARGIN_TOP, gap));
    setNormal(recentlyTopComp, 'margin-bottom', parseCssPx2TailwindRem(MARGIN_BOTTOM, gap));
    delNormal(recentlyTopComp, 'bottom');
  } else {
    setNormal(childComp1, 'margin-top',
      parseCssPx2TailwindRem(MARGIN_TOP, childRect1.top - pRect.top - paddingObj1.top));
  }

  // 下边距：有下邻节点则与之保持间距，同时反向设置下邻的 margin-top
  if (recentlyBottomComp) {
    const gap = recentlyBottomRect.top - childRect1.bottom;
    setNormal(childComp1, 'margin-bottom', parseCssPx2TailwindRem(MARGIN_BOTTOM, gap));
    setNormal(recentlyBottomComp, 'margin-top', parseCssPx2TailwindRem(MARGIN_TOP, gap));
    delNormal(recentlyBottomComp, 'top');
  } else {
    setNormal(childComp1, 'margin-bottom',
      parseCssPx2TailwindRem(MARGIN_BOTTOM, pRect.bottom - childRect1.bottom - paddingObj1.bottom));
  }

  // 重排兄弟顺序：把当前节点插到上邻之后，并修正 children 顺序
  pComp.children = moveAfterByPropLoop(
    pComp.children,
    {prop: childComp1.prop},
    recentlyTopComp ? recentlyTopComp.prop : null
  );
  pComp.children = fixChildrenOrder(pComp.children, comps);
  // 转为文档流后，绝对定位的残留属性要清掉
  delNormal(childComp1, 'left', 'top', 'right', 'bottom');
}

/**
 * 判断第一个节点是否有margin-top，有则父元素必须要设置 上边框 border-t-[0.000000001px] border-t-transparent （已存在则不添加）。处理 CSS 经典现象：**外边距塌陷（margin collapse，外边距合并）**，具体属于：**父子块级元素垂直外边距塌陷**。
 * @param prop 要父节点的prop
 * @param comps AST数组
 * @returns "xx.xxxrem"
 */
export function fixParentMarginCollapse(prop, comps) {

  let sComp = comps.find(c => c.prop === prop);

  // 获取全部子节点
  let children = sComp.children;
  let childrenCompArr = [];     // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let i1 = 0; i1 < children.length; i1++) {
    let cComp = comps.find(c => c.prop === children[i1].prop);
    childrenCompArr.push(cComp)
  }

  // 没有子节点则不需要执行
  if (childrenCompArr.length == 0) {
    return;
  }

  for (let i1 = 0; i1 < childrenCompArr.length; i1++) {
    fixParentMarginCollapse(childrenCompArr[i1].prop, comps)
  }

  // 如果该节点已经设置了边框 border 或者 border-top，则不需要重新设置了
  let normal = sComp.dynamicCss[0].sources[0].css.normal

  if (normal.hasOwnProperty("border-top") || normal.hasOwnProperty("border")) {
    return;
  }

  let childrenNormal = childrenCompArr[0].dynamicCss[0].sources[0].css.normal;

  // 如果第一个子节点没有设置margin或者margin-top,则也不需要设置
  if (!childrenNormal.hasOwnProperty("margin-top") && !childrenNormal.hasOwnProperty("margin")) {
    return;
  }

  _set(sComp, 'dynamicCss[0].sources[0].css.normal.border-top', "border-t-[0.000000001px] border-t-transparent");

}

/**
 * 判断是否有 absolute 布局的子节点
 * @param prop 要父节点的prop
 * @param comps AST数组
 * @returns "xx.xxxrem"
 */
export function hasAbsoluteChildren(prop, comps) {

  // 获取父节点。
  let pComp = comps.find(item => item.prop == prop)

  // 获取该父节点下面的子节点
  let childrenTemp = pComp.children;
  let childCompArr = [];  // 要考虑子节点的顺序，不能直接用 comps.filter 过滤获取
  for (let i1 = 0; i1 < childrenTemp.length; i1++) {
    let cComp = comps.find(item => item.prop === childrenTemp[i1].prop);
    if (cComp) {
      childCompArr.push(cComp)
    }
  }

  // 遍历子节点，判断是否有absolute布局
  for (const child of childCompArr) {
    if (getPositionType(child) == "absolute") {
      return true;
    }
  }
  return false;
}

/**
 * 判断两个视口矩形是否重叠
 */
export function isRectOverlap(rect1, rect2): boolean {

  const noOverlap =
    // rect1.right < rect2.left ||
    // rect1.left > rect2.right ||
    rect1.bottom <= rect2.top ||
    rect1.top >= rect2.bottom;

  return !noOverlap;
}

/**
 *  获取dom的 坐标 和 高宽
 * @param prop 要修复节点的prop
 * @param comps 要修正的AST数组
 * @returns "xx.xxxrem"
 */
export function getBoundingClientRect(dom) {
  return {
    left: convertInlineCss2Number(dom.style.left),
    right: convertInlineCss2Number(dom.style.left) + convertInlineCss2Number(dom.style.width),
    top: convertInlineCss2Number(dom.style.top),
    bottom: convertInlineCss2Number(dom.style.top) + convertInlineCss2Number(dom.style.height),
    width: convertInlineCss2Number(dom.style.width),
    height: convertInlineCss2Number(dom.style.height),
    display: dom.style.display
  }
}


/**
 * 将指定prop的元素，移动或新增到参照prop元素的后面（存在就移动，不存在就新增）
 * @param arr 原始对象数组
 * @param targetProp 需要移动元素的prop值
 * @param refProp 参照元素prop，目标放在它后面；如果传null，则插入到数组第一位
 * @returns 返回新数组
 */
export function moveAfterByPropLoop(arr, target, refProp) {

  const result = [];
  const targetProp = target.prop;

  // 判断 target 是否已经存在，存在则调换元素位置，不存在则插入
  for (const item of arr) {
    if (item.prop === targetProp) {
      target = item;
      break;
    }
  }

  // 参照为null，直接把目标放首位
  if (refProp === null) {
    for (const item of arr) {
      if (item.prop === targetProp) {
        continue;
      }
      result.push(item);
    }
    // 目标元素放最前面
    if (target) {
      result.unshift(target);
    }
    return result;
  }

  // 正常逻辑：放到refProp元素后面
  for (const item of arr) {
    if (item.prop === targetProp) {
      continue;
    }
    result.push(item);
    if (item.prop === refProp && target) {
      result.push(target);
      target = null;
    }
  }

  // 遍历结束还没放置，追加到末尾
  if (target) result.push(target);
  return result;
}


/**
 * 获取节点prop，以及其后代节点的prop
 * @param arr 要返回的数组
 * @param prop
 * @param comps
 */
export const getDescendantsPropArr = (arr, prop, comps) => {

  if (arr.indexOf(prop) == -1) {
    arr.push(prop)
  }
  const comp = comps.find(c => c.prop === prop)
  let children = comp.children
  for (let child of children) {
    getDescendantsPropArr(arr, child.prop, comps);
  }
}

/**
 * @description 检查拖拽后，是否超过父节点的范围
 * @param prop  拖拽节点的prop
 * @param comps 节点
 * @returns
 */
export function checkOutRange(minSelectX, maxSelectX, minSelectY, maxSelectY, parentProp) {

  // 获取父节点的dom
  let pDom = document.getElementById(parentProp === 'root' ? DESIGNBOARD : parentProp);

  // 获取父节点的dom的 left坐标、right坐标、高度、宽度
  let pRect = getBoundingClientRect(pDom);

  return minSelectX >= pRect.left && minSelectY >= pRect.top && maxSelectX <= pRect.right && maxSelectY <= pRect.bottom;
}


/**
 * 取最近的父节点
 * @param arr
 * @returns {*|null}
 */
export function findMinXY(arr, comps) {
  // 如果数组为空，返回 null
  if (!arr || arr.length === 0) {
    return "root";
  }

  // 1.2.2.1、可能性父节点的中心点坐标 + 拖拽坐标 + 勾股定理 获取最近的 可能性父节点 的节点A。
  // 初始化最小值对象为数组中第一个对象
  let minPoint = arr[0];

  let comps2 = handleAllCompsIndex(comps);

  // 遍历数组中的每个对象
  for (let i1 = 1; i1 < arr.length; i1++) {

    let vComp1 = comps2.find(item => item.prop == minPoint.prop);

    let vComp2 = comps2.find(item => item.prop == arr[i1].prop);

    if (compareVersion(vComp1, vComp2) <= 0) {
      minPoint = arr[i1];
    }

  }

  return minPoint.prop;
}

/**
 * 获取 position 值
 * 规则：不存在 / null / "" → 默认 static
 */
export function getPositionType(data) {
  let normal = data.dynamicCss[0].sources[0].css.normal;
  const position = normal["position"];

  if (!normal.hasOwnProperty("position") || position === undefined || position === null || position === "") {
    return "static";
  }
  return position;
}

/**
 * 获取display布局类型
 * 默认值规则：字段不存在 / null / 空字符串 → 视为 block
 * @param data 组件json对象
 * @returns DisplayType
 */
export function getLayoutType(data) {
  const display = (data as any)?.dynamicCss?.[0]?.sources?.[0]?.css?.normal?.display;

  // 不存在、空、null 统一默认 block
  if (display === undefined || display === null || display === "") {
    return "block";
  }
  return display;
}

/**
 * 解析 Tailwind padding 类名字符串，输出上下左右 px 值
 * @param {string} className - padding 的tailwind类，支持多个类空格分隔，例如 'p-4 py-2 pr-[13px]'
 * @param {number} [rootFontSize] - html根字体大小；不传自动读取浏览器真实根字体
 * @returns {{top: number, right: number, bottom: number, left: number}} 上下左右 padding，单位px
 *
 * ✅支持的写法列表：
 * 1. 四周统一： p-0  p-1  p-2.5  p-px
 * 2. 单方向：pt-2 pr-3 pb-4 pl-5
 * 3. 水平/垂直简写：px-2 py-4
 * 4. 方括号任意px值：p-[20px] pt-[8px] px-[30px] py-[12px]
 * 5. 多个类组合：'py-2 px-[18px] pt-6'，后面类会覆盖前面同方向的值
 *
 * ❌不支持：
 * 1. 响应式前缀 md:p-4 / hover:p-2 （会直接忽略该class）
 * 2. 方括号内 rem / % / calc()，仅支持 [数字px] 格式，如[24px]
 * 3. tailwind.config.js theme.spacing 自定义扩展值
 */
export function parseTailwindPadding(className) {
  // 如果没有传入，则自动获取浏览器真实根字体
  const remValue = parseFloat(getRemValue());

  // 初始化上下左右padding，默认全部0px
  let top = 0;
  let right = 0;
  let bottom = 0;
  let left = 0;

  // 分割多个class，按空格切割，去除首尾空白
  const classList = className.trim().split(/\s+/);

  // 遍历每一个padding类名
  for (const cls of classList) {
    // --------------------------
    // ① 匹配方括号任意值语法，例如 pt-[15px]、p-[22px]、px-[30px]
    // 正则分组：完整类名 | 方向(t/r/b/l/x/y) | 括号内数字
    // --------------------------
    const bracketReg = /^(p([trblxy])?)-\[([\d.]+)px\]$/;
    const bracketMatch = cls.match(bracketReg);
    if (bracketMatch) {
      const [_, _prefix, dir, numStr] = bracketMatch;
      const val = Number(numStr); // 取出括号内px数值

      // 根据方向赋值
      switch (dir) {
        case undefined: // p-[xxpx] 四周
          top = right = bottom = left = val;
          break;
        case 't':
          top = val;
          break;
        case 'r':
          right = val;
          break;
        case 'b':
          bottom = val;
          break;
        case 'l':
          left = val;
          break;
        case 'x':
          right = left = val;
          break; // px-[xxpx] 左右
        case 'y':
          top = bottom = val;
          break; // py-[xxpx] 上下
      }
      continue; // 当前类处理完成，进入下一个循环
    }

    // --------------------------
    // ② 匹配普通预设类名 p-4 / pt-2 / px-3 / p-px
    // --------------------------
    const normalReg = /^(p([trblxy])?)-(.+)$/;
    const normalMatch = cls.match(normalReg);
    if (!normalMatch) {
      continue; // 不是padding相关class，跳过
    }
    const [_, _prefix, dir, unitStr] = normalMatch;

    let pxValue;
    if (unitStr === 'px') {
      // tailwind特殊关键字 p-px 代表1px
      pxValue = 1;
    } else {
      // 转为数字，过滤非法值
      const unitNum = parseFloat(unitStr);
      if (isNaN(unitNum)) continue;
      // tailwind公式： unit * 0.25rem，换算px
      pxValue = unitNum * 0.25 * remValue;
    }

    // 根据方向赋值
    switch (dir) {
      case undefined: // p-4 四周全部赋值
        top = right = bottom = left = pxValue;
        break;
      case 't':
        top = pxValue;
        break;
      case 'r':
        right = pxValue;
        break;
      case 'b':
        bottom = pxValue;
        break;
      case 'l':
        left = pxValue;
        break;
      case 'x':
        right = left = pxValue;
        break; // px-* 左右
      case 'y':
        top = bottom = pxValue;
        break; // py-* 上下
    }
  }

  return {top, right, bottom, left};
}

/**
 * 修复子节点的顺序，文档流的都排在最前面，非文档流的都排在文档流的后面
 * @param children AST的节点的 children
 * @returns comps AST数组
 */
export function fixChildrenOrder(children, comps) {

  // 文档流的子节点
  let staticChildren = []
  // 非文档流的子节点
  let notStaticChildren = []

  for (let j1 = 0; j1 < children.length; j1++) {
    let cComp = comps.find(item => item.prop === children[j1].prop);


    if (cComp) {

      if (getPositionType(cComp) == "static" || getPositionType(cComp) == "sticky" || getPositionType(cComp) == "relative") {
        staticChildren.push({prop: cComp.prop});
      } else {
        notStaticChildren.push({prop: cComp.prop});
      }
    }
  }


  for (let i1 = 0; i1 < notStaticChildren.length; i1++) {
    staticChildren.push(notStaticChildren[i1]);
  }

  return staticChildren;
}


/**
 * 给AST的每个节点都添加版本号的下标 index
 * @param comps AST数组
 * @returns comps AST数组
 */
export function handleAllCompsIndex(comps) {

  comps = _cloneDeep(comps);

  handleCompsIndex("root", comps, null, null)

  return comps;
}

/**
 * 给AST的指定的节点都添加版本号的下标 index
 * @param prop 指定的节点prop
 * @param comps AST数组
 * @param parentIndex 父节点的index
 * @param childOrder 子节点的index的最后一位，文档流的都是 1 。非文档流的从2开始，按子节点的顺序依次递增
 * @returns
 */
export function handleCompsIndex(prop, comps, parentIndex, childOrder) {

  let sComp = comps.find(c => c.prop === prop)

  let currentIndex = '1'
  if (prop != 'root') {
    currentIndex = generateTreeNodeIndex(parentIndex, childOrder)
  }
  sComp["index"] = currentIndex
  // 获取该父节点下面的文档流子节点(dragProps是拖拽移动的节点，拖拽移动的节点要默认是绝对布局absolute，忽略以前的定位)
  let childrenTemp = sComp.children;
  let notStaticIndex = 2;
  for (let i1 = 0; i1 < childrenTemp.length; i1++) {
    let cComp = comps.find(item => item.prop === childrenTemp[i1].prop);
    if (cComp) {
      if ((getPositionType(cComp) != "static" && getPositionType(cComp) != "sticky" && getPositionType(cComp) != "relative")) {
        handleCompsIndex(childrenTemp[i1].prop, comps, sComp.index, notStaticIndex++)
      } else {
        handleCompsIndex(childrenTemp[i1].prop, comps, sComp.index, "1")
      }
    }
  }
}

/**
 * 比较两个版本号的下标 index 谁大谁小
 * @param v1 版本号的下标 index
 * @param v2 版本号的下标 index
 * @returns
 */
export function compareVersion(vComp1, vComp2) {
  let v1 = vComp1.index;
  let v2 = vComp2.index;

  const al = v1.split('.').map(Number);
  const a2 = v2.split('.').map(Number);
  // 先比较段数
  if (al.length !== a2.length) {
    return al.length > a2.length ? 1 : -1;
  }
  // 段数相同，逐位比较
  for (let i1 = 0; i1 < al.length; i1++) {
    if (al[i1] < a2[i1]) return -1;
    if (al[i1] > a2[i1]) return 1;
  }
  return 0;
}

/**
 * 处理 flex 和 grid 布局的 obsolute
 * @param pComp flex布局或grid布局的节点
 * @param selectCom absolute布局的节点
 * @param comps AST数组
 * @returns
 */
export function handleFelxGridObsolute(pComp, selectComp, comps) {

  let rectArr = getRectArr(comps);

  if (getPositionType(pComp) == "static" || getPositionType(pComp) == "sticky" && pComp.prop != "root") { // 检查子节点是否有 absolute 布局
    _set(pComp, 'dynamicCss[0].sources[0].css.normal.position', "relative")
    let normal = pComp.dynamicCss[0].sources[0].css.normal
    // 删除 left、top 键
    delete normal["left"];
    delete normal["top"];
  }

  let selectRect = rectArr.find(item => item.prop == selectComp.prop);

  let pRect = rectArr.find(item => item.prop == pComp.prop);

  _set(selectComp, 'dynamicCss[0].sources[0].css.normal.position', "absolute")    // 把拖拽节点改成 absolute 布局
  _set(selectComp, 'dynamicCss[0].sources[0].css.normal.left', parseCssPx2TailwindRem(LEFT, (selectRect.left - pRect.left)))
  _set(selectComp, 'dynamicCss[0].sources[0].css.normal.top', parseCssPx2TailwindRem(TOP, (selectRect.top - pRect.top)))

  let normal = selectComp.dynamicCss[0].sources[0].css.normal
  // 删除 left、top 键
  delete normal.margin;
  delete normal["margin-left"];
  delete normal["margin-rigth"];
  delete normal["margin-top"];
  delete normal["margin-bottom"];


  // 重新设置兄弟节点的顺序关系
  pComp.children = pComp.children.filter(item => item.prop !== selectComp.prop);
  pComp.children.push({prop: selectComp.prop});
  pComp.children = fixChildrenOrder(pComp.children, comps)

}


/**
 * @description 改变宽度或高度后，重新修正父节点枚举高宽 auto 和 子节点枚举高宽 full
 * @param { string[] } prop 组件 id
 * @param comps
 * @returns
 */
export function getRenderProp(prop, comps) {
  const sComp = comps.find(item => item.prop == prop);

  // 根节点（无父节点或父节点不存在）直接返回自己
  const pComp = comps.find(item => item.prop == sComp?.parent);
  if (!pComp) return sComp?.prop;

  // 父节点是 auto 自适应，继续向上找
  const wh = getWidthHeight(pComp);
  if (wh.width == "w-auto" || wh.height == "h-auto") {
    return getRenderProp(pComp.prop, comps);
  }

  return pComp.prop;
}

/**
 * @description 改变宽度或高度后，重新修正父节点枚举高宽 auto 和 子节点枚举高宽 full
 * @param { string[] } prop 组件 id
 * @param comps
 * @returns
 */
export function fixWhitHeight(prop, components, rectArr) {


  let s_comp = components.find(item => item.prop == prop);
  const s_el = document.getElementById(prop)
  let sRect = rectArr.find(item => item.prop == prop);
  let s_dycss = parseToCssDefault(s_comp);

  if (prop != "root") {
    // 宽度处理
    sRect.width = calculateWidth(s_dycss, s_comp, components)

    // 高度处理
    sRect.height = calculateHeight(s_dycss, s_comp, components)

    // left处理
    sRect.left = calculateLeft(s_el, s_dycss, s_comp, components)

    // top处理
    sRect.top = calculateTop(s_el, s_dycss, s_comp, components)


  }

  for (let i = 0; i < s_comp.children.length; i++) {
    const childRef = s_comp.children[i]
    const childComp = components.find(c => c.prop === childRef.prop)
    if (!childComp) continue
    fixWhitHeight(childRef.prop, components, rectArr)
  }

}


/**
 * 获取 高宽 值
 * 规则：不存在 / null / "" → 默认 auto
 */
export function getWidthHeight(data) {
  let normal = data.dynamicCss[0].sources[0].css.normal;

  let width = normal["width"];
  if (!normal.hasOwnProperty("width") || width === undefined || width === null || width === "") {
    width = "w-auto";
  }

  let height = normal["height"];
  if (!normal.hasOwnProperty("height") || height === undefined || height === null || height === "") {
    height = "h-auto";
  }

  return {width: width, height: height};
}

/**
 * 根据 prop 做局部渲染刷新
 */
export function localRecurseCalcCompsXY(renderProp, comps) {
  console.log(renderProp, comps)
  // const renderEl = document.getElementById(renderProp === 'root' ? DESIGNBOARD : renderProp);
  // let dataIndex = renderEl.getAttribute('data-index');
  //
  // // 找到最后一个 . 的索引
  // let index1 = "1";
  // let index2 = 1;
  // const lastIndex = dataIndex.lastIndexOf('.');
  // if (lastIndex === -1) {
  //   // 没有点的情况，前面为空，后面是原字符串
  //   index1 = "";
  //   index2 = parseFloat(dataIndex);
  // } else {
  //   index1 = dataIndex.slice(0, lastIndex);
  //   index2 = parseFloat(dataIndex.slice(lastIndex + 1));
  // }
  //
  // recurseCalcCompsXY(renderProp, false, index1, index2, comps);

}


/**
 * 获取各个节点dom的 left、top、高、宽
 */
export function getRectArr(comps) {
  // 获取每个 DOM 的 left、top、高、宽
  let rectArr = [];
  // 遍历子节点，获取各个节点的div的坐标和高宽
  for (const comp of comps) {
    let compDom = document.getElementById(comp.prop === 'root' ? DESIGNBOARD : comp.prop);
    let compRect = getBoundingClientRect(compDom);
    compRect["prop"] = comp.prop;
    rectArr.push(compRect);
  }
  return rectArr;
}


/**
 * 检查 grid 是否可以继续按顺序填充子元素
 * children = null, gridColumnSize = null, gridRowSize = null, 没有指定时，默认取父节点的
 * @param {string} prop - 父节点的 prop
 * @param {Array} comps - 所有组件数组
 * @param {Array} children - 子节点列表（可选）
 * @param {number} gridColumnSize - 列数（可选）
 * @param {number} gridRowSize - 行数（可选）
 * @param {Array} excludeProps - 需要排除的 prop 列表，如拖拽中的元素（可选）
 * @returns {'canAdd' | 'full' | 'overflow' | null}
 *   'canAdd' - 还有空格，可以继续添加
 *   'full' - 刚好装满，未超出格子
 *   'overflow' - 溢出装不下了，超出了格子
 *   null - 不是 grid 布局
 */
export function checkGridHaveCell(prop, comps, children = null, gridColumnSize = null, gridRowSize = null, excludeProps = []) {
  const pComp = comps.find(item => item.prop === prop);

  if (!pComp || getLayoutType(pComp) !== "grid") return null;

  // 获取父节点的动态CSS
  const p_dycss = parseTailwindToCss(pComp.dynamicCss[0].sources[0].css.normal);

  // 获取列数和行数
  if (gridColumnSize === null) {
    gridColumnSize = parseInt(p_dycss[GRID_TEMPLATE_COLUMNS] || "1");
  }
  if (gridRowSize === null) {
    gridRowSize = parseInt(p_dycss[GRID_TEMPLATE_ROWS] || "1");
  }

  // 创建网格占用表
  const grid = Array.from({length: gridRowSize}, () => Array(gridColumnSize).fill(false));

  if (children === null) {
    children = pComp.children;
  }

  // 获取子节点（保持顺序），排除隐藏节点和排除列表中的节点
  const childCompArr = [];
  for (let i = 0; i < children.length; i++) {
    const cComp = comps.find(item => item.prop === children[i].prop);
    if (!cComp) continue;

    // 排除拖拽中的元素
    if (excludeProps.includes(children[i].prop)) continue;

    const c_dycss = parseToCssDefault(cComp);
    // 跳过隐藏的组件
    if (c_dycss[DISPLAY] === 'none') continue;

    // 只处理文档流元素（static/sticky/relative），非文档流不占用 Grid 格子
    const position = c_dycss[POSITION];
    if (position !== 'static' && position !== 'sticky' && position !== 'relative') continue;

    childCompArr.push({
      comp: cComp,
      dycss: c_dycss,
      colSpan: convertInlineCss2Number(c_dycss[GRID_COLUMN]) || 1,
      rowSpan: convertInlineCss2Number(c_dycss[GRID_ROW]) || 1
    });
  }

  // 如果没有文档流子元素，Grid 为空，可以添加
  if (childCompArr.length === 0) {
    return 'canAdd';
  }

  // Grid 默认填充：从左到右、从上到下（逐行填充），考虑跨行跨列
  let currentRow = 0;
  let currentCol = 0;

  for (const child of childCompArr) {
    const {colSpan, rowSpan} = child;

    // 如果已经超出最大行数，说明溢出了
    if (currentRow >= gridRowSize) {
      return 'overflow';
    }

    // 检查跨行是否超出边界
    if (currentRow + rowSpan > gridRowSize) {
      return 'overflow';
    }

    // 检查当前行剩余列数是否足够
    const remainingCols = gridColumnSize - currentCol;
    if (remainingCols < colSpan) {
      // 换行
      currentRow++;
      currentCol = 0;
      // 换行后再次检查
      if (currentRow >= gridRowSize) {
        return 'overflow';
      }
      if (currentRow + rowSpan > gridRowSize) {
        return 'overflow';
      }
    }

    // 检查当前位置是否能放下（考虑跨行跨列）
    let canPlace = true;
    for (let r = currentRow; r < currentRow + rowSpan && r < gridRowSize && canPlace; r++) {
      for (let c = currentCol; c < currentCol + colSpan && c < gridColumnSize && canPlace; c++) {
        if (grid[r][c]) canPlace = false;
      }
    }

    if (!canPlace) {
      // 当前位置被占用，尝试找下一个可用位置（智能填充）
      let placed = false;
      for (let row = 0; row < gridRowSize && !placed; row++) {
        for (let col = 0; col < gridColumnSize && !placed; col++) {
          // 检查是否可以从该位置放置
          if (row + rowSpan <= gridRowSize && col + colSpan <= gridColumnSize) {
            let canPlaceHere = true;
            for (let r = row; r < row + rowSpan && canPlaceHere; r++) {
              for (let c = col; c < col + colSpan && canPlaceHere; c++) {
                if (grid[r][c]) canPlaceHere = false;
              }
            }
            if (canPlaceHere) {
              // 占用该区域
              for (let r = row; r < row + rowSpan; r++) {
                for (let c = col; c < col + colSpan; c++) {
                  grid[r][c] = true;
                }
              }
              placed = true;
              // 更新当前位置，用于下一个元素
              currentRow = row;
              currentCol = col + colSpan;
              // 如果当前列超出，下一行从头开始
              if (currentCol >= gridColumnSize) {
                currentRow++;
                currentCol = 0;
              }
            }
          }
        }
      }

      if (!placed) {
        // 找不到位置放置，Grid 已满（溢出）
        return 'overflow';
      }
    } else {
      // 正常占用当前位置
      for (let r = currentRow; r < currentRow + rowSpan; r++) {
        for (let c = currentCol; c < currentCol + colSpan; c++) {
          grid[r][c] = true;
        }
      }
      currentCol += colSpan;
      if (currentCol >= gridColumnSize) {
        currentRow++;
        currentCol = 0;
      }
    }
  }

  // 所有子元素都放置完毕，检查是否还有空格
  for (let row = 0; row < gridRowSize; row++) {
    for (let col = 0; col < gridColumnSize; col++) {
      if (!grid[row][col]) {
        return 'canAdd'; // 还有空格，可以继续添加
      }
    }
  }

  // 没有剩余格子，刚好填满
  return 'full';
}

/**
 * 获取父节点的所有子节点
 * 文档流节点排在最前面，非文档流节点依次排在后面
 */
export const getParentAllChildren = (comps, parentComp) => {
  console.log("getParentAllChildren------", comps, parentComp);
  const childrenTemp = parentComp.children ?? [];
  const compMap = new Map(comps.map(item => [item.prop, item]));

  const flowChildren: any[] = [];
  const nonFlowChildren: any[] = [];

  for (const child of childrenTemp) {
    const matched = compMap.get(child.prop);
    if (!matched) continue;

    const positionType = getPositionType(matched);
    if (positionType === 'absolute' || positionType === 'fixed') {
      nonFlowChildren.push(matched);
    } else {
      flowChildren.push(matched);
    }
  }

  return [...flowChildren, ...nonFlowChildren];
}


/**
 * 按 newChildrenComp 的顺序重排 parentDom 下这批子 DOM 的顺序
 *
 * 说明：
 *  - 不区分 position 类型：static / relative / absolute / fixed 全部参与重排。
 *    absolute/fixed 节点自身靠 top/left 定位，改变其 DOM 顺序不会造成视觉跳变；
 *    static/relative 节点的文档流位置由 DOM 顺序决定，重排后布局自然更新。
 *  - 只处理确实是 parentDom 直接子节点的 DOM；跨级、其他父节点下的同名节点一律跳过。
 *  - 使用 anchor + 依次 insertBefore 的方式，避免 DocumentFragment 先把节点摘除，
 *    导致 parentDom 下"不在这批列表里"的节点被挤乱位置。
 *  - parentDom 下若有"不在这批列表里"的节点（例如某些组件内部自动挂载的装饰节点），
 *    它们会保持原相对位置，不会被这次重排打乱。
 *
 * @param parentComp      父节点组件对象；prop 为 'root' 时会映射到 DESIGNBOARD
 * @param newChildrenComp 新的子节点顺序；应传入该父节点下**全部**需要重排的子节点，
 *                        不要只传 flow 子集，否则 flow 之外的节点会被当成"非目标节点"留在原地，
 *                        导致 DOM 顺序与树顺序不一致。
 */
export const reorderChildrenDom = (parentComp: any, newChildrenComp: any[]) => {
  const parentProp = parentComp?.prop === 'root' ? DESIGNBOARD : parentComp?.prop;
  const parentDom = document.getElementById(parentProp);
  if (!parentDom) return;

  const targetDoms: HTMLElement[] = [];
  for (const c of newChildrenComp) {
    const domId = c.prop === 'root' ? DESIGNBOARD : c.prop;
    const dom = document.getElementById(domId);
    if (dom) targetDoms.push(dom);
  }
  if (targetDoms.length < 2) return;
  const realParent = targetDoms[0].parentElement;
  if (!realParent) return;

  const orderedDoms = targetDoms.filter(d => d.parentElement === realParent);
  if (orderedDoms.length < 2) return;

  let anchor: Node | null = null;
  let earliestIndex = Number.POSITIVE_INFINITY;
  for (const dom of orderedDoms) {
    const idx = Array.prototype.indexOf.call(realParent.childNodes, dom);
    if (idx !== -1 && idx < earliestIndex) {
      earliestIndex = idx;
      anchor = dom.previousSibling;
    }
  }

  let ref: Node | null = anchor;
  for (const dom of orderedDoms) {
    const next = ref ? ref.nextSibling : realParent.firstChild;
    if (next !== dom) realParent.insertBefore(dom, next);
    ref = dom;
  }
};
